// MCP, My APIs, Register, Agent Import, Admin Review, Auth, TopUp, Capability
import React, { useState as uxS } from 'react';
import { createPortal } from 'react-dom';
import { XPrim, XIcons } from '../primitives.jsx';
import { XData, XEnv } from '../data.jsx';
import { XApi } from '../services.js';
import { XShell } from '../shell.jsx';
import { XWallet } from '../wallet.js';
import { Dialog, DialogHeader, DialogBody, Select, SelectOption, AiImportDialog } from '../ui/index.js';
import { useT } from '../i18n.jsx';
import { useStripePaymentTracking } from '../components/StripePaymentTracking.jsx';
import { RechargeModeSwitch } from '../components/RechargeModeSwitch.jsx';
import { useHostValidation } from '../use-host-validation.js';
import { LogoUpload } from '../components/LogoUpload.jsx';

// ============ MCP ============
//
// MCP server runs at https://mcp.xapi.to/mcp — it's stateless and always online; users just need
// a client config snippet pointing at it with their API key in the URL. (Mirrors xapi-new design:
// no enable/disable toggle, no "gateway status" — those were fake state in the old v2 mock.)
//
// Each platform has its own config shape:
//   - Cursor / VS Code / Cline / Windsurf / Trae / Warp / Others — `{"mcpServers": {"xapi": {"url": ...}}}`
//   - Claude Code — `claude mcp add` CLI command
//   - Cursor also gets a one-click "cursor://" deep link install button
const MCP_BASE = 'https://mcp.xapi.to/mcp';
const PLATFORMS = [
  { id: 'cursor',   name: 'Cursor',      hasOneClick: true },
  { id: 'vscode',   name: 'VS Code' },
  { id: 'cline',    name: 'Cline' },
  { id: 'windsurf', name: 'Windsurf' },
  { id: 'claude',   name: 'Claude Code', isCli: true },
  { id: 'trae',     name: 'Trae' },
  { id: 'warp',     name: 'Warp' },
  { id: 'others',   name: 'Others' },
];

const buildMcpConfig = (platformId, apiKey) => {
  const key = apiKey || 'YOUR_API_KEY';
  const url = `${MCP_BASE}?apikey=${key}`;
  if (platformId === 'claude') {
    // Claude Code uses a CLI command rather than a JSON snippet.
    return `claude mcp add --transport http xapi "${url}"`;
  }
  if (platformId === 'others') {
    return `# Add to your MCP configuration file:
{
  "mcpServers": {
    "xapi": {
      "url": "${url}"
    }
  }
}

# ${apiKey ? 'Your API key is pre-filled above.' : 'Replace YOUR_API_KEY with your actual API key.'}
# Get your API key at: https://xapi.to/console`;
  }
  return `{
  "mcpServers": {
    "xapi": {
      "url": "${url}"
    }
  }
}`;
};

const buildCursorInstallUrl = (apiKey) => {
  const cfg = JSON.stringify({ url: `${MCP_BASE}?apikey=${apiKey || 'YOUR_API_KEY'}` });
  // Cursor expects base64-encoded config in the deeplink.
  const b64 = (typeof window !== 'undefined' && window.btoa) ? window.btoa(cfg) : Buffer.from(cfg).toString('base64');
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=xapi&config=${b64}`;
};

const MCPPage = () => {
  const { navigate } = XShell.useRoute();
  const { authed } = XShell.useAuth();
  const { t } = useT();
  const [activePlatform, setActivePlatform] = uxS('cursor');
  const envVars = XEnv.useEnv();
  const apiKeyVar = envVars.find(v => v.name === 'XAPI_KEY');
  const keyValue = apiKeyVar && apiKeyVar.value ? apiKeyVar.value : '';
  const hasKey = !!keyValue;

  // Live counts: marketplace APIs + tool count + sandbox key state.
  const [apis, setApis] = uxS(XData.APIS);
  const [sandboxKeys, setSandboxKeys] = uxS(0);
  React.useEffect(() => {
    let cancelled = false;
    XApi.apiSvc.listMarketplace().then(list => {
      if (cancelled || !Array.isArray(list) || !list.length) return;
      setApis(list);
    }).catch(()=>{});
    if (authed) {
      XApi.keyService.list().then(ks => {
        if (cancelled) return;
        setSandboxKeys(ks.filter(k => k.allowSandbox).length);
      }).catch(()=>{});
    }
    return () => { cancelled = true; };
  }, [authed]);

  const totalTools = apis.reduce((s, a) => s + ((a.endpoints && a.endpoints.length) || 0), 0);
  const totalApis = apis.length;
  const config = buildMcpConfig(activePlatform, keyValue);
  const platformInfo = PLATFORMS.find(p => p.id === activePlatform);

  // Subtitle has two number placeholders, so we substitute both. The same `.replace` pattern
  // is used elsewhere in the codebase (console overview welcome string).
  const subtitle = t('mcp.subtitle').replace('{APIS}', String(totalApis)).replace('{TOOLS}', String(totalTools));

  return (
    <div style={{maxWidth: 1480, margin:'0 auto', padding:'28px 40px 80px'}}>
      <div className="micro">{t('mcp.eyebrow')}</div>
      <h1 style={{margin:'8px 0 0', fontSize: 28, letterSpacing:'-0.02em', fontWeight: 500}}>{t('mcp.title')}</h1>
      <p style={{color:'var(--fg-3)', fontSize: 14, marginTop: 8, maxWidth: 720, lineHeight: 1.65}}>
        {subtitle}
      </p>

      {/* Stat strip */}
      <div className="row gap-6" style={{marginTop: 24, paddingBottom: 22, borderBottom: '1px solid var(--line)'}}>
        <div>
          <div className="mono" style={{fontSize: 28, letterSpacing: '-0.02em'}}>{totalApis}</div>
          <div className="micro">{t('mcp.stat.apis')}</div>
        </div>
        <div style={{width: 1, height: 36, background:'var(--line)'}}/>
        <div>
          <div className="mono" style={{fontSize: 28, letterSpacing: '-0.02em'}}>{totalTools}</div>
          <div className="micro">{t('mcp.stat.tools')}</div>
        </div>
        <div style={{width: 1, height: 36, background:'var(--line)'}}/>
        <div>
          <div className="mono" style={{fontSize: 28, letterSpacing: '-0.02em', color:'var(--ok)'}}>{t('mcp.stat.online')}</div>
          <div className="micro">{t('mcp.stat.status')}</div>
        </div>
      </div>

      <div className="mcp-grid" style={{display:'grid', gridTemplateColumns:'320px 1fr', gap: 24, marginTop: 24}}>
        {/* Left: endpoint info + auth + sandbox keys */}
        <div className="col gap-3" style={{minWidth: 0}}>
          <div className="card" style={{padding: 16}}>
            <div className="micro" style={{marginBottom: 8}}>{t('mcp.endpoint.label')}</div>
            <div className="hairline mono" style={{padding:'8px 12px', borderRadius: 5, background:'var(--bg-2)', fontSize: 12, wordBreak:'break-all'}}>{MCP_BASE}</div>
            <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, marginTop: 10, color:'var(--fg-3)', lineHeight: 1.6}}>
              {(() => {
                const parts = t('mcp.endpoint.note').split('{QS}');
                return <>{parts[0]}<span className="mono">?apikey=…</span>{parts[1] || ''}</>;
              })()}
            </div>
          </div>

          <div className="card" style={hasKey ? {padding: 16} : {padding: 16, borderColor: 'var(--warn)'}}>
            <div className="row between" style={{marginBottom: 10}}>
              <div className="micro">{t('mcp.auth.label')}</div>
              <button className="btn btn-sm btn-ghost" onClick={()=>navigate('console', { tab:'env' })}>
                <XIcons.IconExternal size={11}/> {t('mcp.auth.manage')}
              </button>
            </div>
            {hasKey ? (
              <>
                <div className="row gap-2" style={{marginBottom: 10}}>
                  <XIcons.IconCheck size={13} style={{color:'var(--ok)'}}/>
                  <span style={{fontSize: 13}}>{t('mcp.auth.loaded')}</span>
                </div>
                <div className="hairline mono" style={{padding:'8px 12px', borderRadius: 5, background:'var(--bg-2)', fontSize: 12, color:'var(--fg-2)'}}>
                  {keyValue.slice(0, 10) + '…' + keyValue.slice(-4)}
                </div>
                <div className="micro-mono" style={{textTransform:'none', letterSpacing:0, marginTop: 10, color:'var(--fg-4)', lineHeight: 1.6}}>
                  {t('mcp.auth.injected')}
                </div>
              </>
            ) : (
              <>
                <div className="row gap-2" style={{marginBottom: 10}}>
                  <span style={{width: 6, height: 6, borderRadius:'50%', background:'var(--warn)', flexShrink:0}}/>
                  <span style={{fontSize: 13, color:'var(--warn)'}}>{t('mcp.auth.noKey')}</span>
                </div>
                <div className="micro-mono" style={{textTransform:'none', letterSpacing:0, color:'var(--fg-3)', lineHeight: 1.65}}>
                  {(() => {
                    const parts = t('mcp.auth.placeholder').split('{PH}');
                    return <>{parts[0]}<span className="mono">YOUR_API_KEY</span>{parts[1] || ''}</>;
                  })()}
                </div>
                <button className="btn btn-sm btn-primary" style={{marginTop: 10, width:'100%', justifyContent:'center'}} onClick={()=>navigate('console', { tab:'env', focus:'XAPI_KEY' })}>
                  <XIcons.IconKey size={12}/> {t('mcp.auth.set')}
                </button>
              </>
            )}
          </div>

          {authed && (
            <div className="card" style={{padding: 16}}>
              <div className="micro" style={{marginBottom: 4}}>{t('mcp.sandbox.title')}</div>
              <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-3)', lineHeight: 1.6}}>
                {(() => {
                  const parts = t('mcp.sandbox.desc').split('{SANDBOX}');
                  return <>{parts[0]}<span className="mono">Sandbox</span>{parts[1] || ''}</>;
                })()}
              </div>
              <button className="btn btn-sm" style={{marginTop: 10, width:'100%', justifyContent:'center'}} onClick={()=>navigate('console', { tab:'keys' })}>
                {t('mcp.sandbox.manage')} <XIcons.IconArrowRight size={11}/>
              </button>
            </div>
          )}
        </div>

        {/* Right: platform tabs + config */}
        <div className="card" style={{padding: 0, minWidth: 0}}>
          <div style={{borderBottom:'1px solid var(--line)', padding: '4px 4px 0', overflowX:'auto'}}>
            <div className="row" style={{gap: 0}}>
              {PLATFORMS.map(p => (
                <button
                  key={p.id}
                  onClick={()=>setActivePlatform(p.id)}
                  className={`tab ${activePlatform === p.id ? 'active' : ''}`}
                  style={{whiteSpace:'nowrap'}}
                >{p.name}</button>
              ))}
            </div>
          </div>
          <div style={{padding: 18}}>
            <div className="row between" style={{alignItems:'center', marginBottom: 12}}>
              <div className="micro">{platformInfo.isCli ? t('mcp.config.cli') : t('mcp.config.label')}</div>
              <div className="row gap-2">
                {platformInfo.hasOneClick && (
                  <a className="btn btn-sm btn-primary" href={buildCursorInstallUrl(keyValue)} target="_blank" rel="noreferrer">
                    <XIcons.IconBolt size={12}/> {t('mcp.config.oneClick')}
                  </a>
                )}
                <XPrim.CopyBtn text={config} label={platformInfo.isCli ? t('mcp.config.copyCommand') : t('mcp.config.copyConfig')}/>
              </div>
            </div>
            <pre className="code" style={{maxHeight: 380, fontSize: 12.5, lineHeight: 1.65}}>{config}</pre>
            {!hasKey && (
              <div className="row gap-2" style={{marginTop: 14, padding:'10px 12px', borderRadius: 6, background:'rgba(230,178,74,0.08)', border:'1px solid rgba(230,178,74,0.3)'}}>
                <XIcons.IconInfo size={12} style={{color:'var(--warn)', flexShrink: 0, marginTop: 2}}/>
                <span className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-2)', lineHeight: 1.6}}>
                  {(() => {
                    // Three placeholders: YOUR_API_KEY, the link to Console → Keys, and XAPI_KEY mono.
                    // Walk the template, splitting on each token in order, so injection stays language-agnostic.
                    const parts = t('mcp.config.placeholderHint').split(/(\{PH\}|\{LINK\}|\{KEY\})/g);
                    return parts.map((seg, i) => {
                      if (seg === '{PH}')   return <span key={i} className="mono">YOUR_API_KEY</span>;
                      if (seg === '{LINK}') return <a key={i} onClick={()=>navigate('console', { tab:'keys' })} style={{cursor:'pointer', color:'var(--fg-1)', textDecoration:'underline'}}>{t('mcp.config.consoleKeys')}</a>;
                      if (seg === '{KEY}')  return <span key={i} className="mono">XAPI_KEY</span>;
                      return seg;
                    });
                  })()}
                </span>
              </div>
            )}
            <div className="micro-mono" style={{textTransform:'none', letterSpacing:0, marginTop: 14, color:'var(--fg-4)', lineHeight: 1.65}}>
              {platformInfo.isCli
                ? t('mcp.config.cliHint')
                : t('mcp.config.pasteHint').replace('{NAME}', platformInfo.name).replace('{TOOLS}', String(totalTools))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ My APIs ============
// WorkspacePage — provider hub. Matches xapi-new MyApisPage structure (MyApisPage.tsx:210).
//
// Data sources (only two backend calls, per xapi-new audit):
//   GET    /api-services/mine                 → full list
//   DELETE /api-services/{id}                 → delete
//   POST   /api-services/{id}/submit-review   → send DRAFT → SANDBOX + quality check
//
// Grouping into 4 sections matches xapi-new buckets exactly (MyApisPage.tsx:274–283):
//   activeApis   : status === 'ACTIVE'
//   sandboxApis  : status in [SANDBOX, PENDING_REVIEW, APPROVED]
//   draftApis    : status === 'DRAFT'
//   attentionApis: status in [REJECTED, SUSPENDED]
//
// No per-API stats fetch on this page (xapi-new uses placeholder 9.9/490ms/100% values).
// Drafts are server-side only (v2 hasn't implemented local draft persistence yet).
const WorkspacePage = () => {
  const { navigate } = XShell.useRoute();
  const { authed, isEntity, isApiKeyLogin, canProvideApis } = XShell.useAuth();
  const { t } = useT();
  const { toast } = XPrim.useToast();
  const [items, setItems] = uxS([]);
  const [loading, setLoading] = uxS(true);
  const [err, setErr] = uxS(null);
  const [deletingId, setDeletingId] = uxS(null);
  const [reviewDialog, setReviewDialog] = uxS(null);   // { api, submitting, result }
  const [deleteDialog, setDeleteDialog] = uxS(null);   // { api, submitting }
  const [aiImportOpen, setAiImportOpen] = uxS(false);
  const [skillImportOpen, setSkillImportOpen] = uxS(false);

  // Backend rule: /api-services/mine + POST /api-services require @RequireEntity, which
  // additionally rejects loginMethod==='apikey' (see EntityAccountGuard). So workspace
  // access requires BOTH: ENTITY account AND a Twitter-OAuth session. apikey-logged-in
  // entity users get the re-verify CTA below instead of a raw 403 banner.
  //   canProvideApis (= admin whitelist) only unlocks direct publish — surfaced inside
  //   the submit-review dialog.
  const canUseWorkspace = authed && isEntity && !isApiKeyLogin;

  const reload = React.useCallback(async () => {
    if (!canUseWorkspace) { setItems([]); setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const list = await XApi.apiSvc.listMine();
      const adapted = (list || []).map(a => XApi.adapters.apiService(a));
      setItems(adapted);
    } catch (e) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [canUseWorkspace]);

  React.useEffect(() => { reload(); }, [reload]);

  // Status buckets — mirrors xapi-new MyApisPage.tsx:274–283 exactly.
  const buckets = React.useMemo(() => {
    const active = [], sandbox = [], review = [], draft = [], attention = [];
    items.forEach(a => {
      // a.status 现在承载修订生命周期(lifecycleState):DRAFT/SANDBOX/IN_REVIEW/ACTIVE/SUSPENDED
      // (回退兼容旧 ApiService.status 的 PENDING_REVIEW/APPROVED/REJECTED)。
      // SANDBOX(已通过校验、待提交)与 IN_REVIEW(已提交、审核中)分开,别把未提交的当成审核中。
      const s = a.status;
      if (s === 'ACTIVE') active.push(a);
      else if (s === 'SANDBOX') sandbox.push(a);
      else if (s === 'IN_REVIEW' || s === 'PENDING_REVIEW' || s === 'APPROVED') review.push(a);
      else if (s === 'DRAFT') draft.push(a);
      else if (s === 'REJECTED' || s === 'SUSPENDED') attention.push(a);
      else draft.push(a); // fallback: unknown status treated as draft
    });
    return { active, sandbox, review, draft, attention };
  }, [items]);

  // Optimistic delete + invalidate (xapi-new uses React Query, we do local refresh).
  // Native confirm() was jarring on mobile and didn't match the rest of the app's
  // dialog styling — open a Dialog-component confirm instead.
  const onDelete = (a) => setDeleteDialog({ api: a, submitting: false });
  const runDelete = async () => {
    if (!deleteDialog || !deleteDialog.api) return;
    const a = deleteDialog.api;
    setDeleteDialog(d => ({ ...d, submitting: true }));
    setDeletingId(a.id);
    const prev = items;
    setItems(items.filter(x => x.id !== a.id));
    try {
      await XApi.apiSvc.delete(a.id);
      toast('Deleted');
      setDeleteDialog(null);
    } catch (e) {
      setItems(prev);
      toast(e.message || 'Delete failed');
      setDeleteDialog(d => ({ ...d, submitting: false }));
    } finally {
      setDeletingId(null);
    }
  };

  // Submit-for-review flow with quality-check result surfaced (xapi-new SubmitReviewDialog:107–207).
  const openReview = (a) => setReviewDialog({ api: a, submitting: false, result: null });
  const runSubmitReview = async () => {
    if (!reviewDialog || !reviewDialog.api) return;
    setReviewDialog(d => ({ ...d, submitting: true }));
    try {
      const resp = await XApi.apiSvc.submitForReview(reviewDialog.api.id);
      const qc = resp && resp.qualityCheck;
      const passed = !!(qc && qc.passed);
      if (passed) {
        toast(`"${reviewDialog.api.name}" passed quality checks — entered Sandbox.`);
        setReviewDialog(null);
      } else {
        // Keep the dialog open and surface the failed checks — a bare
        // "score: 75" toast gave the user nothing to act on. The backend
        // returns qualityCheck.checks[] with a message per failed check.
        setReviewDialog(d => ({
          ...d,
          submitting: false,
          result: qc || { passed: false, score: null, checks: [] },
        }));
      }
      reload();
    } catch (e) {
      const msg = e.message || 'Failed to submit for review';
      toast(msg);
      setReviewDialog(d => ({ ...d, submitting: false }));
    }
  };

  // Edit is available in any post-draft state EXCEPT while review is in flight.
  // Backend updateApi / updateApiVersionConfig only check ownership, not status. The form already
  // surfaces Hotfix and Release buttons that target the ACTIVE version specifically.
  // 评审进行中(IN_REVIEW，旧 PENDING_REVIEW/APPROVED)不可编辑;其余(DRAFT/SANDBOX/ACTIVE/SUSPENDED)可编辑。
  const canEdit = (status) =>
    status !== 'IN_REVIEW' && status !== 'PENDING_REVIEW' && status !== 'APPROVED';
  // 提交上线要求修订处于 SANDBOX(后端 submit 只接受 SANDBOX)。DRAFT 需先保存进 SANDBOX，
  // 评审失败会回到 SANDBOX,故 SANDBOX 一档即覆盖首发与重提。
  const canSubmit = (status) => status === 'SANDBOX';

  return (
    <div className="workspace-shell" style={{maxWidth: 1480, margin:'0 auto', padding:'28px 40px 80px'}}>
      {/* Header — on desktop the title block + actions sit side-by-side; on
          phones the actions wrap onto their own row below the description so
          neither column gets squeezed (see .workspace-header CSS rule). */}
      <div className="row between workspace-header" style={{alignItems:'flex-end', marginBottom: 24, gap: 16, flexWrap:'wrap'}}>
        <div className="workspace-header-text" style={{minWidth: 0, flex: '1 1 320px'}}>
          <div className="micro">{t('console.nav.workspace')}</div>
          <h1 style={{margin:'8px 0 0', fontSize: 24, letterSpacing:'-0.01em', fontWeight: 500}}>{t('workspace.title')}</h1>
          <p style={{color:'var(--fg-3)', fontSize: 13, marginTop: 6}}>{t('workspace.subtitle')}</p>
        </div>
        <div className="row gap-2 workspace-header-actions" style={{flexShrink: 0}}>
          <button className="btn" onClick={()=>setSkillImportOpen(true)} disabled={!canUseWorkspace}>
            <XIcons.IconZap size={13}/> {t('workspace.import')}
          </button>
          <button className="btn btn-primary" onClick={()=>navigate('workspace-new')} disabled={!canUseWorkspace}>
            <XIcons.IconPlus size={13}/> {t('workspace.provideApi')}
          </button>
        </div>
      </div>

      {!authed && (
        <div className="card" style={{padding: 32, textAlign:'center'}}>
          <p style={{color:'var(--fg-3)', fontSize: 13, marginBottom: 16}}>{t('workspace.empty')}</p>
          <button className="btn btn-primary" onClick={()=>navigate('login')}>{t('common.signIn')}</button>
        </div>
      )}

      {/* Block all paths that can't reach /api-services/mine:
          - virtual / non-entity users → need to upgrade to ENTITY
          - apikey-logged-in entity users → need to re-login via Twitter OAuth
          (Backend's EntityAccountGuard rejects both with a raw 403, so we intercept here.) */}
      {authed && (!isEntity || isApiKeyLogin) && (
        <div className="card" style={{padding: 28, background:'linear-gradient(180deg, rgba(245,165,36,0.08), transparent)', borderColor:'rgba(245,165,36,0.3)'}}>
          <div className="row gap-3" style={{alignItems:'flex-start'}}>
            <div style={{width: 40, height: 40, borderRadius: 8, background:'var(--bg-3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0}}>
              <XIcons.IconTwitter size={16}/>
            </div>
            <div style={{flex: 1}}>
              <h3 style={{margin:'2px 0 8px', fontSize: 16, fontWeight: 500}}>
                {isEntity && isApiKeyLogin ? 'Sign in with X to manage your APIs' : 'Verify with X to publish APIs'}
              </h3>
              <p style={{color:'var(--fg-3)', fontSize: 13, lineHeight: 1.7, margin: '0 0 14px', maxWidth: 620}}>
                {isEntity && isApiKeyLogin
                  ? 'Your account already has X linked, but this session is authenticated with an API key. Provider tools (register / edit / submit-for-review) require a Twitter OAuth session.'
                  : isApiKeyLogin
                    ? 'You\'re signed in with an API key. Link your X (Twitter) handle to unlock the provider Workspace — register APIs, track revenue, manage versions.'
                    : 'Link an X (Twitter) handle to your account to unlock provider tools.'}
              </p>
              <div className="row gap-2">
                <button className="btn btn-primary" onClick={async () => {
                  // Apikey-logged-in ENTITY user: their X handle is already linked, so
                  // we just kick off a fresh Twitter OAuth. The callback's new tokens
                  // overwrite the apikey session via bootstrap.js' consumeCallbackUrl
                  // (tokens.set), so an explicit logout is not needed.
                  // Non-entity users go to /bind for the VIRTUAL → ENTITY OAuth-link flow.
                  if (isEntity && isApiKeyLogin) {
                    try {
                      const r = await XApi.authService.getTwitterAuthorizeUrl();
                      if (r && r.url) { window.location.href = r.url; return; }
                    } catch (e) { toast(e.message || 'Failed to start Twitter sign-in'); return; }
                  }
                  navigate('bind');
                }}>
                  <XIcons.IconTwitter size={13}/> {isEntity && isApiKeyLogin ? 'Sign in with X' : 'Verify with X'}
                </button>
                <button className="btn btn-ghost" onClick={()=>navigate('market')}>Browse marketplace <XIcons.IconArrowRight size={12}/></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canUseWorkspace && (
        <>
          {loading && (
            <div className="card" style={{padding: 32, textAlign:'center'}}>
              <div className="spinner"/> <span style={{marginLeft: 10, color:'var(--fg-3)', fontSize: 13}}>Loading your APIs…</span>
            </div>
          )}

          {!loading && err && (
            <div className="card" style={{padding: 18, color:'var(--err)', fontSize: 13}}>
              {err} <button className="btn btn-sm" style={{marginLeft: 10}} onClick={reload}>Retry</button>
            </div>
          )}

          {!loading && !err && items.length === 0 && (
            <div className="card" style={{padding: 60, textAlign:'center', border:'1px dashed var(--line)', background:'transparent'}}>
              <XIcons.IconCpu size={28} style={{color:'var(--fg-4)'}}/>
              <h3 style={{margin:'12px 0 8px', fontSize: 18, fontWeight: 500}}>No APIs yet</h3>
              <p style={{color:'var(--fg-3)', fontSize: 13, lineHeight: 1.7, maxWidth: 440, margin:'0 auto 16px'}}>
                Register your first API to start earning. Import from OpenAPI or let the AI agent extract endpoints for you.
              </p>
              <div className="row gap-2" style={{justifyContent:'center'}}>
                <button className="btn" onClick={()=>setSkillImportOpen(true)}><XIcons.IconZap size={13}/> {t('workspace.import')}</button>
                <button className="btn btn-primary" onClick={()=>navigate('workspace-new')}><XIcons.IconPlus size={13}/> Provide your first API</button>
              </div>
            </div>
          )}

          {!loading && !err && items.length > 0 && (
            <div className="col gap-4">
              <WorkspaceSection title={t('workspace.section.active')} subtitle={t('workspace.section.activeDesc')} items={buckets.active}
                navigate={navigate} canEdit={canEdit} canSubmit={canSubmit} onDelete={onDelete} onReview={openReview} deletingId={deletingId}/>
              <WorkspaceSection title={t('workspace.section.review')} subtitle={t('workspace.section.reviewDesc')} items={buckets.review}
                navigate={navigate} canEdit={canEdit} canSubmit={canSubmit} onDelete={onDelete} onReview={openReview} deletingId={deletingId}/>
              <WorkspaceSection title={t('workspace.section.sandbox')} subtitle={t('workspace.section.sandboxDesc')} items={buckets.sandbox}
                navigate={navigate} canEdit={canEdit} canSubmit={canSubmit} onDelete={onDelete} onReview={openReview} deletingId={deletingId}/>
              <WorkspaceSection title={t('workspace.section.draft')} subtitle={t('workspace.section.draftDesc')} items={buckets.draft}
                navigate={navigate} canEdit={canEdit} canSubmit={canSubmit} onDelete={onDelete} onReview={openReview} deletingId={deletingId}/>
              <WorkspaceSection title={t('workspace.section.attention')} subtitle={t('workspace.section.attentionDesc')} items={buckets.attention}
                navigate={navigate} canEdit={canEdit} canSubmit={canSubmit} onDelete={onDelete} onReview={openReview} deletingId={deletingId}/>
            </div>
          )}
        </>
      )}

      {reviewDialog && <SubmitReviewDialog
        api={reviewDialog.api}
        submitting={reviewDialog.submitting}
        result={reviewDialog.result}
        onCancel={()=>setReviewDialog(null)}
        onConfirm={runSubmitReview}
      />}

      {deleteDialog && <DeleteApiDialog
        api={deleteDialog.api}
        submitting={deleteDialog.submitting}
        onCancel={()=>setDeleteDialog(null)}
        onConfirm={runDelete}
      />}

      {/* AI import — input/analyzing inline; success → forward to AgentImportPage at review phase */}
      <AiImportDialog
        open={aiImportOpen}
        onClose={()=>setAiImportOpen(false)}
        onAnalyzed={()=>navigate('agent-import', { fromImport: 1 })}
      />

      {/* Register via the xapi-provider agent skill (replaces in-browser AI import as the entry point) */}
      <Dialog open={skillImportOpen} onClose={()=>setSkillImportOpen(false)} maxWidth={520}>
        <DialogHeader title={t('workspace.skillImport.title')} onClose={()=>setSkillImportOpen(false)}/>
        <DialogBody>
          <p style={{fontSize: 13, lineHeight: 1.7, margin:'0 0 16px', color:'var(--fg-2)'}}>{t('workspace.skillImport.desc')}</p>
          <div className="micro" style={{marginBottom: 6}}>{t('workspace.skillImport.installLabel')}</div>
          <div className="row" style={{gap: 8, marginBottom: 16, alignItems:'stretch'}}>
            <code className="mono" style={{flex: 1, minWidth: 0, padding:'8px 10px', background:'var(--bg-2)', borderRadius: 6, fontSize: 12, overflowX:'auto', whiteSpace:'nowrap'}}>npx skills add xapi-labs/xapi-provider</code>
            <button className="btn btn-sm" onClick={()=>navigator.clipboard&&navigator.clipboard.writeText('npx skills add xapi-labs/xapi-provider')}><XIcons.IconCopy size={12}/> {t('workspace.skillImport.copy')}</button>
          </div>
          <div className="micro" style={{marginBottom: 6}}>{t('workspace.skillImport.promptLabel')}</div>
          <code className="mono" style={{display:'block', padding:'8px 10px', background:'var(--bg-2)', borderRadius: 6, fontSize: 12, lineHeight: 1.6, margin:'0 0 16px', color:'var(--fg-2)', whiteSpace:'pre-wrap'}}>{t('workspace.skillImport.promptExample')}</code>
          <a href="https://github.com/xapi-labs/xapi-provider" target="_blank" rel="noreferrer" style={{fontSize: 13, color:'var(--c-mint)', textDecoration:'none'}}>{t('workspace.skillImport.repo')} →</a>
        </DialogBody>
      </Dialog>
    </div>
  );
};

// Grouped section — hides itself if empty so providers only see sections that matter.
const WorkspaceSection = ({ title, subtitle, items, navigate, canEdit, canSubmit, onDelete, onReview, deletingId }) => {
  if (!items || !items.length) return null;
  return (
    <div>
      <div className="row between" style={{marginBottom: 10}}>
        <div>
          <div className="micro" style={{textTransform:'uppercase', letterSpacing:'0.05em'}}>{title} ({items.length})</div>
          {subtitle && <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', marginTop: 2}}>{subtitle}</div>}
        </div>
      </div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap: 10}}>
        {items.map(a => (
          <WorkspaceCard key={a.id} api={a}
            navigate={navigate}
            canEdit={canEdit(a.status)} canSubmit={canSubmit(a.status)}
            isDeleting={deletingId === a.id}
            onDelete={onDelete} onReview={onReview}/>
        ))}
      </div>
    </div>
  );
};

const WorkspaceCard = ({ api, navigate, canEdit, canSubmit, isDeleting, onDelete, onReview }) => {
  const hostDisplay = XApi.helpers.gatewayHost(api.rawHost || api.host, api.integrationMode || api._raw?.integrationMode) || '—';
  return (
    <div className="card card-h" style={{padding: 16, cursor:'pointer', opacity: isDeleting ? 0.5 : 1, filter: isDeleting ? 'grayscale(0.8)' : 'none'}}
      onClick={(e) => {
        if (e.target.closest('[data-no-navigate]')) return;
        // 工作区列表里点卡片进入服务商管理视图，不是公开市场页。
        // 公开页通过卡片上的"预览"按钮另开。
        navigate('workspace-api', { apiId: api.id, section: 'basic' });
      }}>
      <div className="row between" style={{marginBottom: 8}}>
        <div className="row gap-2" style={{minWidth: 0}}>
          <div style={{ width: 28, height: 28, borderRadius: 5, background: 'var(--bg-3)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-mono)', fontSize: 13, color:'var(--fg-1)', flexShrink: 0 }}>
            {(api.name || '?')[0].toUpperCase()}
          </div>
          <div style={{minWidth: 0}}>
            <div style={{fontSize: 14, fontWeight: 500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{api.name || 'Untitled'}</div>
            <div className="mono" style={{fontSize: 11, color:'var(--fg-4)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{hostDisplay}</div>
          </div>
        </div>
        <StatusPill status={api.status}/>
      </div>

      <div className="row gap-3" style={{color:'var(--fg-4)', fontSize: 11, marginTop: 10, flexWrap:'wrap'}}>
        <span className="mono">{api.endpoints || 0} routes</span>
        <span>·</span>
        <span className="mono">{api.version || 'v1'}</span>
        {api.category && <><span>·</span><span>{api.category}</span></>}
        {/* 方案 X: AI 标识 + pending 数 */}
        {api.aiEndpointCount > 0 && (
          <>
            <span>·</span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '1px 6px',
                borderRadius: 4,
                background: 'rgba(0,150,200,0.08)',
                border: '1px solid rgba(0,150,200,0.25)',
                color: 'rgb(0,90,140)',
                fontSize: 10.5,
                fontWeight: 500,
              }}
              title={`${api.aiEndpointCount} AI endpoint${api.aiEndpointCount > 1 ? 's' : ''}`}
            >
              ⚡ AI · {api.aiEndpointCount}
            </span>
            {api.pendingCount > 0 && (
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: 4,
                  background: 'rgba(220,150,0,0.10)',
                  border: '1px solid rgba(220,150,0,0.25)',
                  color: 'rgb(140,90,0)',
                  fontSize: 10.5,
                  fontWeight: 500,
                }}
                title={`${api.pendingCount} model mapping(s) pending admin review`}
              >
                {api.pendingCount} pending
              </span>
            )}
          </>
        )}
      </div>

      <div className="row gap-1" style={{marginTop: 12, justifyContent:'flex-end'}} data-no-navigate>
        {canSubmit && (
          <button className="btn btn-sm" title="Submit for review" onClick={(e)=>{e.stopPropagation(); onReview(api);}}>
            <XIcons.IconArrowRight size={11}/> Submit
          </button>
        )}
        {/* 预览：另开标签打开消费者详情页(owner 可预览未上架的草稿/沙箱)。 */}
        <button className="btn btn-sm btn-ghost" title="预览详情页（消费者视图，新标签打开）"
          onClick={(e)=>{e.stopPropagation(); window.open('/apis/' + api.id, '_blank', 'noopener');}}>
          <XIcons.IconEye size={12}/>
        </button>
        {canEdit && (
          <button className="btn btn-sm btn-ghost" title="Edit" onClick={(e)=>{e.stopPropagation(); navigate('workspace-api', { apiId: api.id, section: 'basic' });}}>
            <XIcons.IconEdit size={12}/>
          </button>
        )}
        <button className="btn btn-sm btn-ghost" title="Delete" disabled={isDeleting}
          onClick={(e)=>{e.stopPropagation(); onDelete(api);}}>
          <XIcons.IconTrash size={12}/>
        </button>
      </div>
    </div>
  );
};

const DeleteApiDialog = ({ api, submitting, onCancel, onConfirm }) => (
  <Dialog open={true} onClose={submitting ? null : onCancel} maxWidth={420}>
    <DialogHeader title="Delete API?" onClose={submitting ? null : onCancel}/>
    <DialogBody>
      <p style={{fontSize: 13, lineHeight: 1.65, margin:'0 0 14px'}}>
        Permanently delete <b>{api.name}</b>. Drafts, versions, and registered
        endpoints will be removed. This cannot be undone.
      </p>
      <div className="row gap-2" style={{justifyContent:'flex-end'}}>
        <button className="btn" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button className="btn" onClick={onConfirm} disabled={submitting}
          style={{borderColor:'var(--err)', color:'var(--err)'}}>
          {submitting ? <><div className="spinner"/> Deleting…</> : <><XIcons.IconTrash size={12}/> Delete</>}
        </button>
      </div>
    </DialogBody>
  </Dialog>
);

const SubmitReviewDialog = ({ api, submitting, result, onCancel, onConfirm }) => {
  // result is set only after a failed run — surface the per-check messages
  // the backend already returns (qualityCheck.checks[]) instead of a number.
  const showResult = !!(result && !result.passed);
  const failed = (result?.checks || []).filter(c => !c.passed);
  const errors = failed.filter(c => c.severity === 'error');
  const warnings = failed.filter(c => c.severity === 'warning');
  return (
    <Dialog open={true} onClose={submitting ? null : onCancel} maxWidth={460}>
      <DialogHeader
        title={showResult ? 'Quality check failed' : 'Submit for review?'}
        onClose={submitting ? null : onCancel}
      />
      <DialogBody>
        {showResult ? (
          <>
            <p style={{fontSize: 13, lineHeight: 1.65, margin:'0 0 14px'}}>
              <b>{api.name}</b> didn't pass — score{' '}
              <span className="mono">{result.score ?? '—'}</span>. Fix the items
              below and resubmit; the API stays editable.
            </p>
            {errors.length > 0 && (
              <div style={{marginBottom: 12}}>
                <div style={{fontSize: 11, textTransform:'uppercase', letterSpacing:'0.04em', color:'var(--err)', marginBottom: 6}}>
                  {errors.length} error{errors.length > 1 ? 's' : ''} — must fix
                </div>
                <ul style={{paddingLeft: 18, margin: 0, fontSize: 12, lineHeight: 1.8, color:'var(--fg-2)'}}>
                  {errors.map((c, i) => <li key={i}>{c.message}</li>)}
                </ul>
              </div>
            )}
            {warnings.length > 0 && (
              <div style={{marginBottom: 12}}>
                <div style={{fontSize: 11, textTransform:'uppercase', letterSpacing:'0.04em', color:'var(--warn)', marginBottom: 6}}>
                  {warnings.length} warning{warnings.length > 1 ? 's' : ''}
                </div>
                <ul style={{paddingLeft: 18, margin: 0, fontSize: 12, lineHeight: 1.8, color:'var(--fg-3)'}}>
                  {warnings.map((c, i) => <li key={i}>{c.message}</li>)}
                </ul>
              </div>
            )}
            <div className="row gap-2" style={{justifyContent:'flex-end'}}>
              <button className="btn" onClick={onCancel} disabled={submitting}>Close</button>
              <button className="btn btn-primary" onClick={onConfirm} disabled={submitting}>
                {submitting ? <><div className="spinner"/> Resubmitting…</> : 'Resubmit'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{fontSize: 13, lineHeight: 1.65, margin:'0 0 14px'}}>
              Send <b>{api.name}</b> through the automated quality check.
            </p>
            <ul style={{paddingLeft: 18, margin:'0 0 14px', color:'var(--fg-3)', fontSize: 12, lineHeight: 1.9}}>
              <li>Passing moves the API to <span className="mono">SANDBOX</span> — visible to admins for final approval.</li>
              <li>Failing returns actionable feedback; the API stays editable.</li>
              <li>Approved APIs enter the public marketplace.</li>
            </ul>
            <div className="row gap-2" style={{justifyContent:'flex-end'}}>
              <button className="btn" onClick={onCancel} disabled={submitting}>Cancel</button>
              <button className="btn btn-primary" onClick={onConfirm} disabled={submitting}>
                {submitting ? <><div className="spinner"/> Submitting…</> : 'Submit for review'}
              </button>
            </div>
          </>
        )}
      </DialogBody>
    </Dialog>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    ACTIVE:   { c: 'var(--ok)', d: 'ok' },
    SANDBOX:  { c: 'var(--warn)', d: 'warn' },
    IN_REVIEW: { c: 'var(--info)', d: '' },
    DRAFT:    { c: 'var(--fg-4)', d: '' },
    PENDING_REVIEW: { c: 'var(--info)', d: '' },
    APPROVED: { c: 'var(--info)', d: '' },
    REJECTED: { c: 'var(--err)', d: 'err' },
    SUSPENDED: { c: 'var(--err)', d: 'err' },
  }[status] || { c: 'var(--fg-4)' };
  return <span className="tag" style={{color: map.c, textTransform:'lowercase'}}><span className="tag-dot" style={{background: map.c}}/>{status.toLowerCase().replace('_',' ')}</span>;
};

// ============ Agent Import ============
// Categories used when backend doesn't provide one. 唯一真相源 = XPrim.SERVICE_CATEGORIES
// （与后端 shared/constants/service-category.ts 一致），后端已对 category 做硬枚举校验，
// 这里必须只发规范 ID，否则创建会被 400 拒绝。
const AGENT_CATEGORIES = XPrim.SERVICE_CATEGORIES;
// 后端无建议分类时的回退默认（取规范集合首项，通用工具兜底）。
const DEFAULT_CATEGORY = 'Public-Utils';

const AgentImportPage = () => {
  const { t } = useT();
  const [logoUploading, setLogoUploading] = uxS(false);
  const { navigate } = XShell.useRoute();
  const { authed } = XShell.useAuth();
  const { toast } = XPrim.useToast();

  // phases: input → analyzing → review → confirming
  const [phase, setPhase] = uxS('input');
  const [rawInput, setRawInput] = uxS('');
  const [log, setLog] = uxS([]);
  const [progress, setProgress] = uxS(0);
  const [err, setErr] = uxS(null);

  // Raw backend response (AgentAnalyzeResponse) — see xapi-new ApiService types:343.
  //   { sessionId, openApiSpec, extractedInfo: { name, description, baseUrl, authType, authHeaderName, suggestedCategory, endpointCount, endpoints }, warnings }
  const [analyzeResult, setAnalyzeResult] = uxS(null);

  // Provider-editable service metadata (seeded from extractedInfo).
  const [meta, setMeta] = uxS({ name:'', description:'', baseUrl:'', host:'', category:DEFAULT_CATEGORY, logoUrl:'' });
  // Host-prefix realtime validation (advisory — backend re-checks on confirm). New
  // registration, so no excludeId. Placed before the `!authed` early return so the
  // hook runs unconditionally.
  const hostValidation = useHostValidation(meta.host);
  // Auth value the provider will keep as a private upstream header (not user-facing).
  const [authValue, setAuthValue] = uxS('');
  // Per-endpoint editable configs (method/path/summary/billingType/costPerCall) keyed by index.
  const [endpointEdits, setEndpointEdits] = uxS({});
  // Per-endpoint test result { status, latencyMs, responseBody, error? } keyed by index.
  const [testResults, setTestResults] = uxS({});
  const [testingIdx, setTestingIdx] = uxS(null);

  const abortRef = React.useRef(null);

  // When AiImportDialog finished and navigated here, restore the analyzed session
  // from sessionStorage and jump straight to the 'review' phase.
  React.useEffect(() => {
    let stash;
    try {
      const raw = sessionStorage.getItem('xapi.aiImport.session');
      if (!raw) return;
      stash = JSON.parse(raw);
      sessionStorage.removeItem('xapi.aiImport.session');
    } catch { return; }
    if (!stash || !stash.resp) return;
    const resp = stash.resp;
    const info = (resp && resp.extractedInfo) || {};
    const endpoints = Array.isArray(info.endpoints) ? info.endpoints : [];
    setAnalyzeResult({ sessionId: resp.sessionId, openApiSpec: resp.openApiSpec, extractedInfo: info, warnings: resp.warnings || [] });
    setMeta({
      name: info.name || '',
      description: info.description || '',
      baseUrl: info.baseUrl || '',
      host: String(info.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32),
      category: info.suggestedCategory || DEFAULT_CATEGORY,
      logoUrl: '',
    });
    const seed = {};
    endpoints.forEach((ep, i) => {
      seed[i] = { method: ep.method, path: ep.path, summary: ep.summary || '', billingType: 'PER_CALL', costPerCall: '0.01' };
    });
    setEndpointEdits(seed);
    setRawInput(stash.rawInput || '');
    setPhase('review');
  }, []);

  if (!authed) return (
    <div style={{maxWidth: 900, margin:'0 auto', padding:'40px 32px 80px'}}>
      <div className="micro">Provider · AI Import</div>
      <h1 style={{margin:'8px 0 0', fontSize: 28, letterSpacing:'-0.02em', fontWeight: 500}}>Sign in to use AI Import</h1>
      <button className="btn btn-primary" style={{marginTop: 20}} onClick={()=>navigate('login')}>Sign in</button>
    </div>
  );

  const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);

  const startAnalyze = async () => {
    if (!rawInput.trim()) { setErr('Paste an OpenAPI spec, URL, or doc snippet first.'); return; }
    setErr(null); setLog([]); setProgress(2); setPhase('analyzing');
    setAnalyzeResult(null); setEndpointEdits({}); setTestResults({}); setAuthValue('');
    abortRef.current = new AbortController();

    try {
      let p = 5;
      const resp = await XApi.agentService.analyzeStream(rawInput, (progressData) => {
        // Backend emits { stage, message, tokens? } per progress event.
        const msg = progressData && (progressData.message || progressData.stage) || 'progress';
        setLog(l => l.concat([typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 120)]));
        p = Math.min(95, p + 6);
        setProgress(p);
      }, { signal: abortRef.current.signal });
      setProgress(100);

      const info = (resp && resp.extractedInfo) || {};
      const endpoints = Array.isArray(info.endpoints) ? info.endpoints : [];
      setAnalyzeResult({ sessionId: resp.sessionId, openApiSpec: resp.openApiSpec, extractedInfo: info, warnings: resp.warnings || [] });

      // Seed metadata from extractedInfo.
      setMeta({
        name: info.name || '',
        description: info.description || '',
        baseUrl: info.baseUrl || '',
        host: slugify(info.name || ''),
        category: info.suggestedCategory || DEFAULT_CATEGORY,
        logoUrl: '',
      });

      // Seed endpoint edits — default billing is PER_CALL with cost=0.01.
      const seed = {};
      endpoints.forEach((ep, i) => {
        seed[i] = {
          method: ep.method,
          path: ep.path,
          summary: ep.summary || '',
          billingType: 'PER_CALL',
          costPerCall: '0.01',
        };
      });
      setEndpointEdits(seed);

      setPhase('review');
    } catch (e) {
      if (e && (e.name === 'AbortError' || e.code === 20)) { setErr('Canceled'); setPhase('input'); }
      else { setErr(e.message || 'Analyze failed'); setPhase('input'); }
    } finally { abortRef.current = null; }
  };

  const cancelAnalyze = () => { try { abortRef.current && abortRef.current.abort(); } catch {} };

  // Run one endpoint's live test. Backend expects:
  //   { sessionId, endpointIndex, privateHeaders?, testParams?, testHeaders?, testBody? }
  const runTest = async (idx) => {
    const info = analyzeResult && analyzeResult.extractedInfo;
    const ep = info && info.endpoints && info.endpoints[idx];
    if (!analyzeResult || !ep) { toast('Session expired — re-analyze'); return; }
    setTestingIdx(idx);
    setTestResults(r => ({ ...r, [idx]: { loading: true } }));

    const privateHeaders = {};
    if (info.authType && info.authType !== 'NONE' && info.authHeaderName && authValue.trim()) {
      privateHeaders[info.authHeaderName] = authValue.trim();
    }
    // testParams: combine path+query values from the param schemas, use examples when present.
    const testParams = {};
    const pickExample = (meta) => {
      if (!meta) return '';
      if (meta.example != null) return String(meta.example);
      if (meta.value != null) return String(meta.value);
      if (meta.schema && meta.schema.default != null) return String(meta.schema.default);
      return '';
    };
    Object.entries(ep.pathParams || {}).forEach(([k, m]) => { const v = pickExample(m); if (v) testParams[k] = v; });
    Object.entries(ep.params || {}).forEach(([k, m]) => { const v = pickExample(m); if (v) testParams[k] = v; });
    const testHeaders = {};
    Object.entries(ep.headers || {}).forEach(([k, m]) => { const v = pickExample(m); if (v) testHeaders[k] = v; });
    const testBody = ep.bodySchema && ep.bodySchema.example !== undefined ? ep.bodySchema.example : undefined;

    try {
      const r = await XApi.agentService.test({
        sessionId: analyzeResult.sessionId,
        endpointIndex: idx,
        privateHeaders: Object.keys(privateHeaders).length ? privateHeaders : undefined,
        testParams: Object.keys(testParams).length ? testParams : undefined,
        testHeaders: Object.keys(testHeaders).length ? testHeaders : undefined,
        testBody,
      });
      setTestResults(res => ({ ...res, [idx]: r }));
    } catch (e) {
      setTestResults(res => ({ ...res, [idx]: { success: false, statusCode: 0, latencyMs: 0, responseBody: null, error: e.message || 'Test failed' } }));
    } finally { setTestingIdx(null); }
  };

  const confirm = async () => {
    if (logoUploading) { toast(t('logo.pending')); return; }
    if (!analyzeResult || !analyzeResult.sessionId) { toast('Session expired — re-analyze'); return; }
    if (!meta.name || !meta.host || !meta.category) { toast('Name, host and category are required'); return; }
    if (!/^[a-z0-9-]+$/.test(meta.host) || meta.host.length < 2) { toast('Host must be lowercase letters, digits, hyphens (≥2 chars)'); return; }
    // Soft-block on a known-invalid host (reserved / blacklisted / taken) — immediate
    // reason instead of a server 400 round-trip. Backend stays authoritative on confirm.
    if (hostValidation.status === 'invalid') { toast(hostValidation.message); return; }

    const info = analyzeResult.extractedInfo || {};
    const privateHeaders = {};
    if (info.authType && info.authType !== 'NONE' && info.authHeaderName && authValue.trim()) {
      privateHeaders[info.authHeaderName] = authValue.trim();
    }

    // Confirm payload matches xapi-new contract (apiService.ts:667):
    //   endpointConfigs[idx] = { method, path, summary, billingType, costPerCall }
    const endpointConfigs = {};
    Object.keys(endpointEdits).forEach(k => {
      const v = endpointEdits[k];
      endpointConfigs[k] = {
        method: v.method,
        path: v.path,
        summary: v.summary,
        billingType: v.billingType,
        costPerCall: parseFloat(v.costPerCall) || 0,
      };
    });

    setPhase('confirming');
    try {
      const created = await XApi.agentService.confirm({
        sessionId: analyzeResult.sessionId,
        name: meta.name,
        description: meta.description || undefined,
        baseUrl: meta.baseUrl || undefined,
        logoUrl: meta.logoUrl || undefined,
        host: meta.host,
        category: meta.category,
        privateHeaders: Object.keys(privateHeaders).length ? privateHeaders : undefined,
        endpointConfigs,
      });
      toast('API created as DRAFT');
      navigate('workspace-api', { apiId: created.id, section: 'basic' });
    } catch (e) { toast(e.message || 'Confirm failed'); setPhase('review'); }
  };

  const info = analyzeResult && analyzeResult.extractedInfo;
  const endpoints = (info && info.endpoints) || [];
  const needsAuth = info && info.authType && info.authType !== 'NONE';
  const setEpEdit = (idx, patch) => setEndpointEdits(e => ({ ...e, [idx]: { ...(e[idx] || {}), ...patch } }));

  return (
    <div style={{maxWidth: 920, margin:'0 auto', padding:'40px 32px 80px'}}>
      <div className="micro">Provider · AI Import</div>
      <h1 style={{margin:'8px 0 0', fontSize: 28, letterSpacing:'-0.02em', fontWeight: 500}}>Let AI build it for you</h1>
      <p style={{color:'var(--fg-3)', fontSize: 14, marginTop: 8, maxWidth: 640}}>
        Paste an OpenAPI spec, raw docs, or a URL. The agent extracts endpoints, infers auth and pricing,
        then drops a DRAFT into your project.
      </p>

      {phase === 'input' && (
        <div className="card" style={{padding: 28, marginTop: 28}}>
          <label className="field-label">Source</label>
          <textarea className="input mono" style={{minHeight: 220, fontSize: 12}}
            placeholder={'Paste OpenAPI/Swagger JSON or YAML, a Postman collection, raw API docs, or a single URL like https://api.example.com/openapi.json'}
            value={rawInput} onChange={e=>{setRawInput(e.target.value); setErr(null);}}/>
          {err && <div style={{color:'var(--err)', fontSize: 12, marginTop: 10}}>{err}</div>}
          <div className="row between" style={{marginTop: 18}}>
            <div className="micro-mono">accepts: openapi 3.x · swagger 2.x · postman v2 · raw text · single URL</div>
            <button className="btn btn-primary btn-lg" onClick={startAnalyze} disabled={!rawInput.trim()}>
              <XIcons.IconZap size={14}/> Analyze
            </button>
          </div>
        </div>
      )}

      {phase === 'analyzing' && (
        <div className="card" style={{padding: 32, marginTop: 28}}>
          <div className="row between" style={{alignItems:'flex-start'}}>
            <div className="row gap-3">
              <div className="spinner" style={{width: 22, height: 22}}/>
              <div>
                <div style={{fontSize: 15}}>Analyzing with AI agent…</div>
                <div className="micro-mono">{Math.round(progress)}%</div>
              </div>
            </div>
            <button className="btn btn-sm" onClick={cancelAnalyze}><XIcons.IconX size={11}/> Cancel</button>
          </div>
          <div className="bar-track" style={{marginTop: 18}}><div className="bar-fill" style={{width: progress+'%', transition:'width 0.3s ease'}}/></div>
          <div className="mono" style={{marginTop: 20, fontSize: 12, color:'var(--fg-3)', lineHeight: 1.8, maxHeight: 260, overflowY:'auto'}}>
            {log.map((l, i) => (
              <div key={i} style={{color: i === log.length-1 ? 'var(--fg-1)' : 'var(--fg-4)'}}>
                <span style={{color:'var(--ok)'}}>✓</span> {l}
              </div>
            ))}
            {!log.length && <div style={{color:'var(--fg-4)'}}>Streaming progress events from agent…</div>}
          </div>
        </div>
      )}

      {phase === 'review' && analyzeResult && (
        <>
          {/* Warnings from the agent */}
          {analyzeResult.warnings && analyzeResult.warnings.length > 0 && (
            <div className="card" style={{padding: 14, marginTop: 20}}>
              <div className="micro" style={{marginBottom: 8}}>Agent notes</div>
              <div className="col gap-2">
                {analyzeResult.warnings.map((w, i) => {
                  const color = w.level === 'error' ? 'var(--err)' : w.level === 'warn' ? 'var(--warn)' : 'var(--fg-3)';
                  return (
                    <div key={i} className="row gap-2" style={{fontSize: 12.5, color: 'var(--fg-2)'}}>
                      <span className="mono" style={{color, fontSize: 10, textTransform:'uppercase'}}>[{w.level}]</span>
                      <span>{w.message}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Metadata form */}
          <div className="card" style={{padding: 24, marginTop: 16}}>
            <div className="row between" style={{marginBottom: 14}}>
              <span className="tag" style={{color:'var(--ok)'}}><XIcons.IconCheck size={10}/> Spec extracted</span>
              <span className="micro-mono">{endpoints.length} endpoints</span>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap: 14}}>
              <div><label className="field-label">Display name</label>
                <input className="input" value={meta.name} onChange={e=>setMeta({...meta, name:e.target.value})}/>
              </div>
              <div><label className="field-label">Slug (host prefix)</label>
                <input className="input mono" value={meta.host} onChange={e=>setMeta({...meta, host:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-')})}/>
                {hostValidation.status !== 'idle' && (
                  <div className="row gap-1" role="status" aria-live="polite" style={{ marginTop: 6, alignItems:'center', fontSize: 11.5, color: hostValidation.status === 'ok' ? 'var(--ok)' : hostValidation.status === 'invalid' ? 'var(--err)' : 'var(--fg-3)' }}>
                    {hostValidation.status === 'ok' ? <XIcons.IconCheck size={12}/> : hostValidation.status === 'invalid' ? <XIcons.IconX size={12}/> : <XIcons.IconInfo size={12}/>}
                    <span>{hostValidation.message}</span>
                  </div>
                )}
              </div>
              <div><label className="field-label">Base URL</label>
                <input className="input mono" value={meta.baseUrl} onChange={e=>setMeta({...meta, baseUrl:e.target.value})}/>
              </div>
              <div><label className="field-label">Category</label>
                <select className="input" value={meta.category} onChange={e=>setMeta({...meta, category:e.target.value})}>
                  {AGENT_CATEGORIES.map(c => <option key={c} value={c}>{c.replace('-', ' ')}</option>)}
                </select>
              </div>
              <div style={{gridColumn:'span 2'}}>
                <label className="field-label">{t('register.overview.logoUrl')}</label>
                <LogoUpload value={meta.logoUrl} onChange={logoUrl => setMeta(current => ({ ...current, logoUrl }))} onUploadingChange={setLogoUploading} />
              </div>
              <div style={{gridColumn:'span 2'}}><label className="field-label">Description</label>
                <textarea className="input" rows={3} value={meta.description} onChange={e=>setMeta({...meta, description:e.target.value})}/>
              </div>
            </div>
          </div>

          {/* Auth (only if agent detected an auth requirement) */}
          {needsAuth && (
            <div className="card" style={{padding: 18, marginTop: 16}}>
              <div className="row between" style={{alignItems:'center', marginBottom: 10}}>
                <div>
                  <div className="micro">Authentication</div>
                  <div style={{fontSize: 13, marginTop: 2, color:'var(--fg-2)'}}>
                    Detected <span className="mono">{info.authType}</span>{info.authHeaderName && <> via <span className="mono">{info.authHeaderName}</span></>}
                  </div>
                </div>
              </div>
              <label className="field-label">Value (stored as a private upstream header)</label>
              <input className="input mono" type="password" value={authValue} onChange={e=>setAuthValue(e.target.value)} placeholder="sk-…"/>
              <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', marginTop: 6}}>
                Used for Test below and injected server-side on every upstream call. Never exposed to end users.
              </div>
            </div>
          )}

          {/* Endpoints — each row editable + testable + per-row billing */}
          <div className="card" style={{marginTop: 16, overflow:'hidden'}}>
            <div className="row between" style={{padding:'12px 18px', borderBottom:'1px solid var(--line)'}}>
              <div className="micro">Endpoints · {endpoints.length}</div>
              <div className="micro-mono" style={{textTransform:'none', letterSpacing:0}}>Edit inline, then Test before Publish</div>
            </div>
            <div style={{maxHeight: 560, overflowY:'auto'}}>
              {endpoints.map((ep, i) => {
                const e = endpointEdits[i] || {};
                const t = testResults[i];
                return (
                  <div key={i} style={{padding:'14px 18px', borderBottom: i < endpoints.length - 1 ? '1px solid var(--line)' : 'none'}}>
                    <div className="row gap-2" style={{marginBottom: 10}}>
                      <select className="input mono" style={{width: 100}} value={e.method || ep.method} onChange={ev=>setEpEdit(i, { method: ev.target.value })}>
                        {['GET','POST','PUT','PATCH','DELETE'].map(m=><option key={m}>{m}</option>)}
                      </select>
                      <input className="input mono" style={{flex:1}} value={e.path || ep.path} onChange={ev=>setEpEdit(i, { path: ev.target.value })}/>
                      <button className="btn btn-sm" onClick={()=>runTest(i)} disabled={testingIdx === i}>
                        {testingIdx === i ? <><div className="spinner"/> Testing…</> : <><XIcons.IconPlay size={11}/> Test</>}
                      </button>
                    </div>
                    <input className="input" style={{marginBottom: 10}} value={e.summary || ''} placeholder="Summary" onChange={ev=>setEpEdit(i, { summary: ev.target.value })}/>

                    <div className="row gap-2" style={{alignItems:'center'}}>
                      <label className="field-label" style={{marginBottom: 0, minWidth: 90}}>Billing</label>
                      <select className="input mono" style={{width: 160}} value={e.billingType || 'PER_CALL'} onChange={ev=>setEpEdit(i, { billingType: ev.target.value })}>
                        <option value="PER_CALL">PER_CALL</option>
                        <option value="PER_TOKEN">PER_TOKEN</option>
                      </select>
                      {e.billingType !== 'PER_TOKEN' && (
                        <>
                          <span style={{fontSize: 12, color:'var(--fg-3)'}}>Cost</span>
                          <input className="input mono" style={{width: 120}} type="number" step="0.0001" min="0" value={e.costPerCall || '0'} onChange={ev=>setEpEdit(i, { costPerCall: ev.target.value })}/>
                          <span style={{fontSize: 11, color:'var(--fg-4)'}}>USD / call</span>
                        </>
                      )}
                      {e.billingType === 'PER_TOKEN' && (
                        <span className="micro-mono" style={{textTransform:'none', letterSpacing:0, color:'var(--fg-4)'}}>
                          Token pricing configured in the editor after publish.
                        </span>
                      )}
                    </div>

                    {/* Test result card */}
                    {t && !t.loading && (
                      <div className="card" style={{marginTop: 10, padding: 10, background:'var(--bg-2)', borderColor: t.success ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}}>
                        <div className="row gap-2" style={{fontSize: 12, marginBottom: 6}}>
                          <span className="mono" style={{color: t.success ? 'var(--ok)' : 'var(--err)'}}>{t.statusCode || 0}</span>
                          {t.latencyMs != null && <span className="micro-mono">{t.latencyMs}ms</span>}
                          {t.error && <span style={{color:'var(--err)', fontSize: 12}}>{t.error}</span>}
                        </div>
                        {t.responseBody != null && (
                          <pre className="code" style={{margin: 0, fontSize: 11, maxHeight: 140, overflowY:'auto'}}>
                            {typeof t.responseBody === 'string' ? t.responseBody : JSON.stringify(t.responseBody, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {!endpoints.length && <div style={{padding: 24, textAlign:'center', color:'var(--fg-3)', fontSize: 13}}>Agent did not detect any endpoints — try a fuller spec.</div>}
            </div>
          </div>

          <div className="row between" style={{marginTop: 22}}>
            <button className="btn" onClick={()=>{ setPhase('input'); setAnalyzeResult(null); }}><XIcons.IconRefresh size={12}/> Start over</button>
            <button className="btn btn-primary btn-lg" onClick={confirm} disabled={logoUploading || !meta.name || !meta.host || !endpoints.length}>
              Create draft <XIcons.IconArrowRight size={13}/>
            </button>
          </div>
        </>
      )}

      {phase === 'confirming' && (
        <div className="card" style={{padding: 32, marginTop: 28, textAlign:'center'}}>
          <div className="spinner" style={{width: 22, height: 22, margin:'0 auto'}}/>
          <div style={{marginTop: 12, fontSize: 13, color:'var(--fg-3)'}}>Creating draft API…</div>
        </div>
      )}
    </div>
  );
};

// ============ Admin Review ============
const AdminPage = () => {
  const { navigate } = XShell.useRoute();
  const { authed } = XShell.useAuth();
  const { toast } = XPrim.useToast();
  const [filter, setFilter] = uxS('PENDING_REVIEW');
  const [items, setItems] = uxS([]);
  const [sel, setSel] = uxS(null);
  const [loading, setLoading] = uxS(true);
  const [err, setErr] = uxS(null);
  const [reviewNote, setReviewNote] = uxS('');
  const [acting, setActing] = uxS(false);
  const reasonRef = React.useRef(null);
  // Mobile-only modal flag. On desktop the right column always renders sel.
  // On phones we hide the right column via CSS and instead pop a Dialog
  // when the user taps a list row.
  const [mobileOpen, setMobileOpen] = uxS(false);
  // Full admin detail (endpoints / openApiSpec / reviews history / provider
  // reputation) — fetched when a list row is selected, since the
  // /admin/pending-reviews list endpoint only returns thin data.
  const [full, setFull] = uxS(null);
  const [fullLoading, setFullLoading] = uxS(false);
  const [fullErr, setFullErr] = uxS(null);
  // Endpoint detail expansion — admin can drill into one endpoint at a time.
  const [openEndpointId, setOpenEndpointId] = uxS(null);
  // Quality-check / OpenAPI raw spec panel toggles — collapsed by default
  // to keep the page short; expand on demand.
  const [showSpec, setShowSpec] = uxS(false);
  React.useEffect(() => {
    if (!sel?.id) { setFull(null); return; }
    let cancelled = false;
    setFullLoading(true); setFullErr(null);
    XApi.adminService.serviceFull(sel.id)
      .then((d) => { if (!cancelled) setFull(d); })
      .catch((e) => { if (!cancelled) setFullErr(e?.message || 'Failed to load full detail'); })
      .finally(() => { if (!cancelled) setFullLoading(false); });
    return () => { cancelled = true; };
  }, [sel?.id]);

  const reload = React.useCallback(async () => {
    if (!authed) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const raw = await XApi.adminService.pendingReviews(filter === 'all' ? undefined : filter);
      // Backend may return [] | {data:[…]} | null on 403 — normalise so
      // .map below never blows up. Without this, a non-admin opening this
      // page used to throw "list.map is not a function" and crash the page
      // (instead of showing the err banner).
      const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      // SANDBOX/ACTIVE filters return ApiService rows with `provider` joined
      // as a User object ({id, twitterScreenName, twitterName, twitterAvatar})
      // rather than a flat handle string — so we coerce to string here. Without
      // this the JSX renders the object directly and React throws "Objects are
      // not valid as a React child", crashing the whole page.
      const stringifyProvider = (p, fallbackUserId) => {
        if (!p) return fallbackUserId || '—';
        if (typeof p === 'string') return p;
        if (typeof p === 'object') {
          const handle = p.twitterScreenName || p.screenName;
          if (handle) return `@${handle}`;
          return p.twitterName || p.name || p.id || fallbackUserId || '—';
        }
        return String(p);
      };
      const adapted = list.map(a => {
        const safeDate = a?.updatedAt || a?.createdAt;
        let submitted = '—';
        try { submitted = safeDate ? XApi.helpers.formatRelative(safeDate) : '—'; } catch { submitted = '—'; }
        return {
          id: a?.id,
          name: a?.name || '(untitled)',
          provider: stringifyProvider(a?.provider, a?.userId),
          host: a?.host || '—',
          category: a?.category || '—',
          status: a?.status,
          submitted,
          score: a?.qualityScore || 0,
          errors: a?.errors || 0,
          warnings: a?.warnings || 0,
          _raw: a || {},
        };
      });
      setItems(adapted);
      setSel(adapted[0] || null);
    } catch (e) {
      setItems([]); setSel(null);
      setErr(e?.message || 'Failed to load (admin access required)');
    }
    finally { setLoading(false); }
  }, [authed, filter]);

  React.useEffect(() => { reload(); }, [reload]);

  // Lock body scroll while the mobile drawer is open so swipes don't bleed
  // through to the page underneath.
  React.useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const decide = async (decision) => {
    if (!sel) return;
    setActing(true);
    try {
      await XApi.adminService.review(sel.id, decision, reviewNote || undefined);
      toast({ title: decision === 'APPROVED' ? 'API approved' : 'API rejected', type: 'success' });
      setReviewNote('');
      reload();
    } catch (e) { toast({ title: e.message || 'Action failed', type: 'error' }); }
    finally { setActing(false); }
  };

  const setStatus = async (status) => {
    if (!sel) return;
    const reason = (reviewNote || '').trim();
    if (!reason) {
      toast({ title: 'Reason required — fill the note field below', type: 'error' });
      reasonRef.current?.focus();
      reasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setActing(true);
    try {
      await XApi.adminService.setStatus(sel.id, status, reason);
      toast({
        title: status === 'SUSPENDED' ? 'API suspended' : status === 'SANDBOX' ? 'API moved to sandbox' : `Status set to ${status}`,
        type: 'success',
      });
      setReviewNote('');
      reload();
    } catch (e) { toast({ title: e.message || 'Action failed', type: 'error' }); }
    finally { setActing(false); }
  };

  const list = items;

  if (!authed) return (
    <div style={{padding: 60, textAlign:'center'}}>
      <h2 style={{margin:'0 0 8px', fontSize: 18, fontWeight: 500}}>Admin only</h2>
      <p style={{color:'var(--fg-3)', fontSize: 13}}>Sign in with an admin account to review pending APIs.</p>
      <button className="btn btn-primary" style={{marginTop: 16}} onClick={()=>navigate('login')}>Sign in</button>
    </div>
  );

  // List row click — set selection AND open the mobile sheet (the sheet is
  // hidden on desktop via Dialog's portal CSS, but we still set the flag so
  // tapping a row on a phone surfaces the detail panel as a popup).
  const onRowClick = (r) => {
    setSel(r);
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 720px)').matches) {
      setMobileOpen(true);
    }
  };

  // Detail content extracted once, rendered into both the desktop main column
  // and the mobile Dialog body. Action buttons live at the top of this block
  // when used in <main>; on mobile the Dialog footer renders Reject/Approve
  // separately so they stay reachable below the scrollable body.
  const DetailContent = sel ? (
    <>
      {/* Title row + action buttons (desktop layout). On mobile this row
          collapses — actions are mirrored at the bottom of the dialog. */}
      <div className="row between admin-detail-titlerow" style={{ flexWrap:'wrap', gap: 14 }}>
        <div style={{ minWidth: 0, flex: '1 1 240px' }}>
          <div className="micro">{(sel.status||'').replace('_',' ')} · {sel.submitted}</div>
          <h1 style={{margin:'6px 0 0', fontSize: 24, letterSpacing:'-0.01em', fontWeight: 500}}>{sel.name}</h1>
          <div className="row gap-3 admin-detail-meta" style={{marginTop: 8, fontSize: 12, color:'var(--fg-3)', flexWrap:'wrap'}}>
            <span>{sel.provider}</span><span>·</span>
            <span className="mono" style={{wordBreak:'break-all'}}>{XApi.helpers.gatewayHost(sel.host, sel.integrationMode)}</span><span>·</span>
            <span>{sel.category}</span>
          </div>
        </div>
        <div className="row gap-2 admin-detail-actions-desktop">
          {/* Open the same /apis/{slug} detail page that public users see, but
              with ?asAdmin=true so the backend's findOne lets us through the
              non-ACTIVE visibility wall. The detail page itself detects the
              flag and renders an "审核模式" banner + enables Try-It for testing. */}
          {full?.activeVersion?.endpoints?.length > 0 && (
            <button
              className="btn"
              onClick={() => {
                const slug = (full.host && full.host.split('.')[0]) || full.id;
                navigate('api-detail', { id: slug, asAdmin: 'true' });
              }}
              title="在 API 详情页打开（含 Try-It，可测试调用）">
              <XIcons.IconExternal size={13}/> 在详情页打开测试
            </button>
          )}
          {sel.status === 'ACTIVE' ? (
            <>
              <button className="btn" disabled={acting} onClick={()=>setStatus('SANDBOX')}><XIcons.IconArrowLeft size={13}/> Move to Sandbox</button>
              <button className="btn" style={{borderColor:'var(--warn)', color:'var(--warn)'}} disabled={acting} onClick={()=>setStatus('SUSPENDED')}><XIcons.IconPause size={13}/> Suspend</button>
            </>
          ) : (
            <>
              <button className="btn" style={{borderColor:'var(--err)', color:'var(--err)'}} disabled={acting} onClick={()=>decide('REJECTED')}><XIcons.IconX size={13}/> Reject</button>
              <button className="btn btn-primary" disabled={acting} onClick={()=>decide('APPROVED')}><XIcons.IconCheck size={13}/> Approve</button>
            </>
          )}
        </div>
      </div>

      <div className="admin-detail-stats" style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap: 12, marginTop: 24}}>
        <div className="card" style={{padding: 16}}><div className="micro">Quality score</div><div className="mono" style={{fontSize: 28, marginTop: 6, color: sel.score >= 80 ? 'var(--ok)' : 'var(--warn)'}}>{sel.score}</div></div>
        <div className="card" style={{padding: 16}}><div className="micro">Errors</div><div className="mono" style={{fontSize: 28, marginTop: 6, color: sel.errors > 0 ? 'var(--err)' : 'var(--fg-1)'}}>{sel.errors}</div></div>
        <div className="card" style={{padding: 16}}><div className="micro">Warnings</div><div className="mono" style={{fontSize: 28, marginTop: 6, color: sel.warnings > 0 ? 'var(--warn)' : 'var(--fg-1)'}}>{sel.warnings}</div></div>
        <div className="card" style={{padding: 16}}><div className="micro">Endpoints</div><div className="mono" style={{fontSize: 28, marginTop: 6}}>{(sel._raw && sel._raw.endpoints && sel._raw.endpoints.length) || '—'}</div></div>
      </div>

      <div className="card" style={{marginTop: 16}}>
        <div style={{padding:'14px 18px', borderBottom:'1px solid var(--line)'}} className="micro">Submission details</div>
        <div className="admin-detail-fields" style={{padding: 18, display:'grid', gridTemplateColumns:'1fr 1fr', gap: 14, fontSize: 13}}>
          {(() => {
            const av = full?.activeVersion;
            return (
              <>
                <div><div className="micro">Base URL</div><div className="mono" style={{marginTop: 4, wordBreak:'break-all'}}>{av?.baseUrl || sel._raw?.baseUrl || '—'}</div></div>
                <div><div className="micro">Auth type</div><div className="mono" style={{marginTop: 4}}>{av?.authType || sel._raw?.authType || '—'}{av?.authConfigConfigured ? ' · configured' : ''}</div></div>
                <div><div className="micro">Visibility</div><div className="mono" style={{marginTop: 4}}>{full?.isPublic ?? sel._raw?.isPublic ? 'PUBLIC' : 'PRIVATE'}</div></div>
                <div><div className="micro">Access mode</div><div className="mono" style={{marginTop: 4}}>{full?.accessMode || sel._raw?.accessMode || 'PROXY'}</div></div>
                {av && Object.keys(av.privateHeaders || {}).length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div className="micro">Private headers (values redacted)</div>
                    <div className="mono" style={{ marginTop: 4, color: 'var(--fg-3)' }}>
                      {Object.keys(av.privateHeaders).map(k => `${k}: ***`).join(', ')}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Provider reputation — count of active services / total / past rejections.
          Quick signal: serial offender vs first-time submitter. */}
      {full?.providerStats && (
        <div className="card" style={{marginTop: 16, padding: 16}}>
          <div className="micro" style={{marginBottom: 10}}>Provider reputation</div>
          <div className="row gap-4" style={{ flexWrap: 'wrap' }}>
            <div style={{ minWidth: 110 }}>
              <div className="micro" style={{ color: 'var(--fg-4)' }}>Active</div>
              <div className="mono" style={{ fontSize: 18, color: 'var(--ok)' }}>{full.providerStats.activeServices}</div>
            </div>
            <div style={{ minWidth: 110 }}>
              <div className="micro" style={{ color: 'var(--fg-4)' }}>Total submissions</div>
              <div className="mono" style={{ fontSize: 18 }}>{full.providerStats.totalServices}</div>
            </div>
            <div style={{ minWidth: 110 }}>
              <div className="micro" style={{ color: 'var(--fg-4)' }}>Rejected</div>
              <div className="mono" style={{ fontSize: 18, color: full.providerStats.rejectedSubmissions > 0 ? 'var(--err)' : 'var(--fg-3)' }}>
                {full.providerStats.rejectedSubmissions}
              </div>
            </div>
            {full.provider?.twitterScreenName && (
              <div style={{ minWidth: 200 }}>
                <div className="micro" style={{ color: 'var(--fg-4)' }}>Provider handle</div>
                <div className="mono" style={{ fontSize: 13, marginTop: 2 }}>
                  @{full.provider.twitterScreenName}
                  {full.provider.createdAt && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--fg-4)' }}>
                      joined {String(full.provider.createdAt).slice(0, 10)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Endpoints — full list with billing/price/streaming per row. Click to
          expand for full configuration (params, body, responses). */}
      {full?.activeVersion?.endpoints && (
        <div className="card" style={{marginTop: 16}}>
          <div className="row between" style={{padding:'14px 18px', borderBottom:'1px solid var(--line)'}}>
            <div className="micro">Endpoints · {full.activeVersion.endpoints.length}</div>
            <div className="micro" style={{color:'var(--fg-4)'}}>OpenAPI spec / billing config / response examples</div>
          </div>
          <div>
            {full.activeVersion.endpoints.map((ep) => {
              const expanded = openEndpointId === ep.id;
              const bt = ep.billingType || 'PER_CALL';
              const priceLabel = (() => {
                if (bt === 'PER_CALL') return ep.costPerCall > 0 ? `$${ep.costPerCall}` : 'free';
                if (bt === 'PER_RESOURCE') return ep.costPerResource > 0 ? `$${ep.costPerResource}/each (${ep.resourceCountJsonPath || 'n/a'})` : 'free';
                if (bt === 'PER_TOKEN') {
                  const tp = ep.tokenPricing || {};
                  const def = tp.default || Object.values(tp)[0];
                  if (def) return `$${def.inputPricePerToken}/tok in · $${def.outputPricePerToken}/tok out`;
                  return 'per-token (unconfigured)';
                }
                return '—';
              })();
              return (
                <div key={ep.id} style={{borderBottom:'1px solid var(--line)'}}>
                  <button type="button" onClick={() => setOpenEndpointId(expanded ? null : ep.id)}
                    className="row gap-3" style={{width:'100%', textAlign:'left', padding:'12px 18px', background:'transparent', border:'none', color:'inherit', cursor:'pointer', alignItems:'center'}}>
                    <XPrim.MethodChip method={ep.method}/>
                    <span className="mono" style={{flex:'2 1 200px', fontSize: 12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{ep.path}</span>
                    <span style={{flex:'1 1 140px', fontSize: 12, color:'var(--fg-2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{ep.name || <span style={{color:'var(--fg-4)'}}>(unnamed)</span>}</span>
                    <span className="mono" style={{fontSize: 10, padding:'2px 6px', borderRadius: 4, background:'var(--bg-2)', border:'1px solid var(--line)', color:'var(--fg-2)'}}>{bt.replace('PER_','').toLowerCase()}</span>
                    <span className="mono" style={{flex:'1 1 180px', fontSize: 11, color:'var(--fg-2)', textAlign:'right'}}>{priceLabel}</span>
                    <XIcons.IconChevronRight size={11} style={{color:'var(--fg-4)', transform: expanded ? 'rotate(90deg)' : 'none', transition:'transform .1s'}}/>
                  </button>
                  {expanded && (
                    <div style={{padding:'4px 18px 16px 18px', background:'var(--bg-1)'}}>
                      {ep.description && <div style={{ fontSize: 12, color:'var(--fg-2)', marginBottom: 12, lineHeight: 1.6 }}>{ep.description}</div>}
                      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap: 14, fontSize: 12}}>
                        {ep.params && Object.keys(ep.params).length > 0 && (
                          <div>
                            <div className="micro">Query params</div>
                            <div className="mono" style={{ marginTop: 4, color:'var(--fg-2)' }}>{Object.keys(ep.params).join(', ')}</div>
                          </div>
                        )}
                        {ep.pathParams && Object.keys(ep.pathParams).length > 0 && (
                          <div>
                            <div className="micro">Path params</div>
                            <div className="mono" style={{ marginTop: 4, color:'var(--fg-2)' }}>{Object.keys(ep.pathParams).join(', ')}</div>
                          </div>
                        )}
                        {ep.headers && Object.keys(ep.headers).length > 0 && (
                          <div>
                            <div className="micro">Public headers</div>
                            <div className="mono" style={{ marginTop: 4, color:'var(--fg-2)' }}>{Object.keys(ep.headers).join(', ')}</div>
                          </div>
                        )}
                        {ep.privateHeaders && Object.keys(ep.privateHeaders).length > 0 && (
                          <div>
                            <div className="micro">Private headers (redacted)</div>
                            <div className="mono" style={{ marginTop: 4, color:'var(--fg-3)' }}>{Object.keys(ep.privateHeaders).join(', ')}</div>
                          </div>
                        )}
                        {ep.bodySchema && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div className="micro">Body schema</div>
                            <XPrim.JsonViewer value={ep.bodySchema} maxHeight={200}/>
                          </div>
                        )}
                        {ep.responses && ep.responses.length > 0 && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div className="micro">Response examples ({ep.responses.length})</div>
                            <XPrim.JsonViewer value={ep.responses} maxHeight={240}/>
                          </div>
                        )}
                        {ep.asyncMode && ep.asyncMode !== 'NONE' && (
                          <div>
                            <div className="micro">Async mode</div>
                            <div className="mono" style={{ marginTop: 4, color:'var(--c-mint)' }}>{ep.asyncMode}{ep.taskStrategy ? ` · ${ep.taskStrategy}` : ''}</div>
                          </div>
                        )}
                        {ep.supportsStreaming && (
                          <div>
                            <div className="micro">Streaming</div>
                            <div className="mono" style={{ marginTop: 4, color:'var(--c-mint)' }}>{ep.streamFormat || 'sse'}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* OpenAPI spec raw viewer — collapsed by default. */}
      {full?.activeVersion?.openApiSpec && (
        <div className="card" style={{marginTop: 16}}>
          <button type="button" onClick={() => setShowSpec(v => !v)}
            className="row between" style={{padding:'14px 18px', width:'100%', background:'transparent', border:'none', borderBottom: showSpec ? '1px solid var(--line)' : 'none', cursor:'pointer', color:'inherit', alignItems:'center'}}>
            <span className="micro">OpenAPI spec (raw)</span>
            <XIcons.IconChevronRight size={11} style={{ color:'var(--fg-4)', transform: showSpec ? 'rotate(90deg)' : 'none', transition: 'transform .1s' }}/>
          </button>
          {showSpec && (
            <div style={{ padding: 14 }}>
              <XPrim.JsonViewer value={full.activeVersion.openApiSpec} maxHeight={420}/>
            </div>
          )}
        </div>
      )}

      {/* Reviews history — past auto-checks + reviewer decisions */}
      {full?.reviews && full.reviews.length > 0 && (
        <div className="card" style={{marginTop: 16}}>
          <div className="micro" style={{padding:'14px 18px', borderBottom:'1px solid var(--line)'}}>
            Review history · {full.reviews.length}
          </div>
          {full.reviews.map((rv) => (
            <div key={rv.id} style={{ padding:'12px 18px', borderBottom:'1px solid var(--line)' }}>
              <div className="row between" style={{ alignItems:'center', flexWrap:'wrap', gap: 10 }}>
                <div className="row gap-2" style={{ alignItems:'center' }}>
                  <span className="mono" style={{
                    fontSize: 10, padding:'2px 6px', borderRadius: 4,
                    background:
                      rv.status === 'APPROVED' ? 'rgba(46,204,113,0.12)' :
                      rv.status === 'REJECTED' ? 'rgba(239,68,68,0.12)' :
                      'var(--bg-2)',
                    color:
                      rv.status === 'APPROVED' ? 'var(--ok)' :
                      rv.status === 'REJECTED' ? 'var(--err)' :
                      'var(--fg-3)',
                    border:'1px solid var(--line)',
                  }}>{rv.status}</span>
                  {typeof rv.qualityScore === 'number' && (
                    <span className="mono" style={{ fontSize: 11, color: rv.qualityScore >= 70 ? 'var(--ok)' : 'var(--warn)' }}>
                      score {rv.qualityScore}/100
                    </span>
                  )}
                  {rv.reviewer?.twitterScreenName && (
                    <span style={{ fontSize: 11, color:'var(--fg-3)' }}>
                      by @{rv.reviewer.twitterScreenName}
                    </span>
                  )}
                </div>
                <span className="micro-mono" style={{ color: 'var(--fg-4)' }}>{String(rv.createdAt).slice(0, 19).replace('T', ' ')}</span>
              </div>
              {rv.reviewNote && (
                <div style={{ marginTop: 6, fontSize: 12, color:'var(--fg-2)', lineHeight: 1.6, whiteSpace:'pre-wrap' }}>
                  {rv.reviewNote}
                </div>
              )}
              {rv.autoChecks && (
                <details style={{ marginTop: 6 }}>
                  <summary style={{ fontSize: 11, color:'var(--fg-4)', cursor:'pointer' }}>Auto-check details</summary>
                  <XPrim.JsonViewer value={rv.autoChecks} maxHeight={200}/>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {fullLoading && (
        <div className="card" style={{marginTop: 16, padding: 20, textAlign:'center'}}>
          <div className="spinner"/> <span style={{marginLeft: 8, color:'var(--fg-3)', fontSize: 12}}>Loading full submission details…</span>
        </div>
      )}
      {fullErr && (
        <div className="card" style={{marginTop: 16, padding: 14, color:'var(--err)', fontSize: 12}}>
          {fullErr}
        </div>
      )}

      <div className="card" style={{marginTop: 16, padding: 18}}>
        <div className="micro">
          {sel.status === 'ACTIVE' ? 'Reason (required for Suspend / Sandbox)' : 'Review note (sent to provider)'}
        </div>
        <textarea ref={reasonRef} className="input" style={{marginTop: 10, minHeight: 90}}
          placeholder={sel.status === 'ACTIVE' ? 'Why are you taking this API down?' : 'Optional notes or feedback…'}
          value={reviewNote} onChange={e=>setReviewNote(e.target.value)}/>
      </div>
    </>
  ) : null;

  return (
    <div className="admin-shell" style={{display:'grid', gridTemplateColumns:'360px 1fr', minHeight:'calc(100vh - 52px)'}}>
      <aside className="admin-aside" style={{borderRight:'1px solid var(--line)', background:'var(--bg-1)'}}>
        <div style={{padding:'16px 18px 0', borderBottom:'1px solid var(--line)'}}>
          <div className="micro">Admin · Review queue</div>
          <h2 style={{margin:'6px 0 12px', fontSize: 18, fontWeight: 500}}>Pending ({list.length})</h2>
          {/* Status tabs — same .tabs/.tab pattern used elsewhere on the
              site (api-detail Try-It / Endpoints / Changelog), so the
              treatment reads as a real tab strip with an underline accent
              instead of three loose pill buttons. */}
          <div className="tabs" style={{borderBottom: 'none'}}>
            {[
              { k: 'PENDING_REVIEW', l: 'Pending review' },
              { k: 'SANDBOX',        l: 'Sandbox' },
              { k: 'ACTIVE',         l: 'Active' },
            ].map(s => (
              <div key={s.k} className={`tab ${filter===s.k?'active':''}`} onClick={()=>setFilter(s.k)}>{s.l}</div>
            ))}
          </div>
        </div>
        {loading && <div style={{padding: 20, textAlign:'center'}}><div className="spinner"/></div>}
        {!loading && err && <div style={{padding: 18, color:'var(--err)', fontSize: 12}}>{err}</div>}
        {!loading && !err && list.length === 0 && <div style={{padding: 32, textAlign:'center', color:'var(--fg-3)', fontSize: 13}}>No items in this state.</div>}
        <div>
          {list.map(r => (
            <button key={r.id} onClick={()=>onRowClick(r)} style={{
              width:'100%', textAlign:'left', padding:'14px 18px',
              borderBottom:'1px solid var(--line)',
              background: sel && sel.id === r.id ? 'var(--bg-3)' : 'transparent',
              borderLeft: sel && sel.id === r.id ? '2px solid var(--fg-0)' : '2px solid transparent',
              cursor:'pointer',
            }}>
              <div className="row between"><div style={{fontSize: 13}}>{r.name}</div><span className="micro-mono">{r.submitted}</span></div>
              <div className="row gap-2" style={{marginTop: 6}}>
                <span className="micro-mono">{r.provider}</span>
                <span className="micro-mono">·</span>
                <span className="micro-mono" style={{color: r.errors > 0 ? 'var(--err)' : r.warnings > 0 ? 'var(--warn)' : 'var(--ok)'}}>
                  score {r.score}
                </span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      <main className="admin-main" style={{padding: '28px 32px'}}>
        {!sel ? (
          <div style={{color:'var(--fg-3)', textAlign:'center', padding: 80}}>Select an API to review</div>
        ) : DetailContent}
      </main>

      {/* Mobile detail drawer. Slides in from the right on desktop, from the
          bottom on phones — same .task-detail-drawer pattern used by the
          Console async tasks tab so the platform has one consistent slide
          panel. Reject/Approve sit in the sticky header (per UX request) so
          they're always reachable; the scrollable body holds the rest. */}
      {!!sel && mobileOpen && typeof document !== 'undefined' && createPortal(
        <>
          <div className="mobile-sheet-backdrop" onClick={() => setMobileOpen(false)} />
          <div className="task-detail-drawer" role="dialog" aria-label="Review API submission" onClick={(e)=>e.stopPropagation()}>
            {/* Sticky header — title + close + Reject/Approve. Action buttons
                go up here (not the bottom) at user request. */}
            <div style={{padding:'14px 18px', borderBottom:'1px solid var(--line)', background:'var(--bg-1)'}}>
              <div className="row between" style={{alignItems:'center', gap: 10}}>
                <div className="micro" style={{color:'var(--fg-3)'}}>Review submission</div>
                <button onClick={() => setMobileOpen(false)} className="btn btn-sm btn-ghost btn-icon" aria-label="Close">
                  <XIcons.IconX size={12}/>
                </button>
              </div>
              <div className="row gap-2" style={{marginTop: 12}}>
                {sel.status === 'ACTIVE' ? (
                  <>
                    <button className="btn" style={{flex: 1, justifyContent:'center'}} disabled={acting || !sel} onClick={()=>setStatus('SANDBOX')}>
                      <XIcons.IconArrowLeft size={13}/> Sandbox
                    </button>
                    <button className="btn" style={{borderColor:'var(--warn)', color:'var(--warn)', flex: 1, justifyContent:'center'}} disabled={acting || !sel} onClick={()=>setStatus('SUSPENDED')}>
                      <XIcons.IconPause size={13}/> Suspend
                    </button>
                  </>
                ) : (
                  <>
                    <button className="btn" style={{borderColor:'var(--err)', color:'var(--err)', flex: 1, justifyContent:'center'}} disabled={acting || !sel} onClick={()=>decide('REJECTED')}>
                      <XIcons.IconX size={13}/> Reject
                    </button>
                    <button className="btn btn-primary" style={{flex: 1, justifyContent:'center'}} disabled={acting || !sel} onClick={()=>decide('APPROVED')}>
                      <XIcons.IconCheck size={13}/> Approve
                    </button>
                  </>
                )}
              </div>
            </div>
            <div style={{padding: 18, overflowY:'auto', flex: 1}}>
              {DetailContent}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

// ============ Login ============
// Inline OAuth-bind step. Used inside the QuickStart wizard (after the user has saved their
// new API key). Single primary CTA: "Continue with X" → /auth/twitter/link → window.location.
// Skippable: virtual accounts work everywhere except provider features.
//
// Activity nudge at the bottom: "Want $1 free credit? Tweet for it →" surfaces the promo
// without making it a required step.
// LoginPage — three sign-in methods, all routed through src/api/auth.js.
//   1. Quick start  → POST /auth/register (auto-login with returned key) — three-step wizard:
//                       (a) save key (Copy or Download required), (b) optional claim, (c) console
//   2. Twitter OAuth → GET /auth/twitter/authorize → redirect; backend lands back with ?accessToken
//   3. API key       → POST /auth/login/apikey
//
// Wallet sign-in is intentionally not offered. Persists referral code so the OAuth redirect honors it.
const REFERRAL_KEY = 'xapi_referral_code';

const LoginPage = () => {
  const { navigate, route } = XShell.useRoute();
  const forFreeApi = route.params?.reason === 'free-api';
  const auth = XShell.useAuth();
  const { toast } = XPrim.useToast();
  const { t } = useT();

  const [apiKey, setApiKey] = uxS('');
  const [referral, setReferral] = uxS(() => {
    try { return sessionStorage.getItem(REFERRAL_KEY) || ''; } catch { return ''; }
  });
  const [showReferral, setShowReferral] = uxS(!!referral);
  const [busy, setBusy] = uxS(null);   // 'apikey' | 'quick' | 'twitter' | null
  const [err, setErr] = uxS(null);

  // Surface backend OAuth-failure messages (e.g. /login?error=... callback) on first render.
  React.useEffect(() => {
    (async () => {
      try {
        const { takePendingError } = await import('../api/bootstrap.js');
        const msg = takePendingError();
        if (msg) setErr(msg);
      } catch {}
    })();
  }, []);
  // QuickStart wizard — null | { step: 'save'|'bind', apiKey, saved }
  // Tweet-based claim flow has moved to /activity ("post tweet for $1 credit"). Account
  // upgrade is now a single OAuth click in the bind step.
  const [wizard, setWizard] = uxS(null);

  const stashReferral = () => {
    try {
      if (referral) sessionStorage.setItem(REFERRAL_KEY, referral);
      else sessionStorage.removeItem(REFERRAL_KEY);
    } catch {}
  };

  const submitKey = async (e) => {
    e && e.preventDefault();
    setErr(null);
    if (!apiKey.trim()) { setErr(t('auth.login.apiKey.required')); return; }
    setBusy('apikey');
    try {
      const trimmed = apiKey.trim();
      const signedInUser = await auth.loginWithApiKey(trimmed);
      // Default the local XAPI_KEY env to the signed-in key — but only if the user hasn't
      // already set one (preserves a preferred key chosen during a previous Twitter session).
      try { XEnv.setIfEmpty({ name: 'XAPI_KEY', value: trimmed, scope: 'global', description: 'Auto-set on API key login' }); } catch {}
      // Stash plaintext for in-session OAuth auto-enable on legacy keys (xapi-new parity).
      XApi.userKeyStash.set(trimmed);
      toast('Signed in');
      if (forFreeApi && signedInUser?.accountType !== 'ENTITY') navigate('bind');
      else if (forFreeApi) navigate('console', { tab: 'env', focus: 'XAPI_KEY' });
      else navigate('console');
    } catch (ex) {
      if (ex.name === 'AbortError') return;
      setErr(ex.message || 'Login failed');
    } finally { setBusy(null); }
  };

  const quickStart = async () => {
    setErr(null); setBusy('quick');
    try {
      const r = await auth.quickStart({ autoLogin: true, referralCode: referral || undefined });
      // QuickStart creates a fresh account whose env scope is brand-new — but use setIfEmpty
      // anyway in case the user is restarting QuickStart against an existing local env.
      try { XEnv.setIfEmpty({ name: 'XAPI_KEY', value: r.apiKey, scope: 'global', description: 'Auto-set on account creation' }); } catch {}
      // Stash plaintext for OAuth auto-enable (xapi-new parity).
      if (r.apiKey) XApi.userKeyStash.set(r.apiKey);
      try { sessionStorage.removeItem(REFERRAL_KEY); } catch {}
      setWizard({
        step: 'save',
        apiKey: r.apiKey,
        saved: false,
      });
    } catch (ex) {
      setErr(ex.message || 'Could not create account');
    } finally { setBusy(null); }
  };

  const useTwitter = async () => {
    setErr(null); setBusy('twitter');
    stashReferral();
    try {
      const { url } = await XApi.authService.getTwitterAuthorizeUrl(referral || undefined);
      if (!url) throw new Error('Twitter authorize URL missing');
      window.location.href = url;   // backend lands us back with ?accessToken
    } catch (ex) {
      setErr(ex.message || 'Twitter login unavailable');
      setBusy(null);
    }
  };

  // ---- QuickStart wizard handlers ----
  const copyKey = () => {
    try { navigator.clipboard?.writeText(wizard.apiKey); } catch {}
    setWizard(w => ({ ...w, saved: true }));
    toast('Key copied to clipboard');
  };
  const downloadKey = () => {
    try {
      const blob = new Blob([
        `# xapi.to API key — keep this safe; we cannot show it again.\n`,
        `# Created: ${new Date().toISOString()}\n\n`,
        `XAPI_KEY=${wizard.apiKey}\n`,
      ], { type: 'text/plain' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `xapi-key-${new Date().toISOString().slice(0,10)}.env`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
      setWizard(w => ({ ...w, saved: true }));
      toast('Key downloaded');
    } catch (ex) { toast('Download failed: ' + (ex.message || ex)); }
  };
  const skipToConsole = () => { setWizard(null); navigate('console'); };

  // Skip the intermediate "Step 2 of 2" card — go straight from "key saved" to
  // the Twitter OAuth URL. The user's already auto-logged in via auth.quickStart
  // (autoLogin: true in this file's quickStart()), so getTwitterLinkUrl works
  // immediately. The wizard stays mounted (preserves the spinner UI) until the
  // browser navigates away to x.com.
  const continueWithXDirect = async () => {
    setWizard(w => ({ ...w, busy: true, err: null }));
    try {
      const r = await XApi.authService.getTwitterLinkUrl();
      if (!r?.url) throw new Error('No OAuth URL returned');
      window.location.href = r.url;
    } catch (ex) {
      setWizard(w => ({ ...w, busy: false, err: ex.message || 'Bind failed' }));
    }
  };

  // ---- Step (a) of QuickStart wizard: save API key (Copy or Download required) ----
  if (wizard && wizard.step === 'save') {
    return (
      <div style={{minHeight:'calc(100vh - 52px)', display:'flex', alignItems:'center', justifyContent:'center', padding: 32}}>
        <div className="card" style={{padding: 36, width: 540, position:'relative', overflow:'hidden'}}>
          <div className="grid-bg" style={{position:'absolute', inset:0, opacity: 0.3}}/>
          <div style={{position:'relative'}}>
            <XPrim.Logo/>
            <div className="row gap-2" style={{margin:'18px 0 6px'}}>
              <span className="micro">{t('auth.wizard.eyebrow')}</span>
            </div>
            <h1 style={{margin:'0 0 6px', fontSize: 22, letterSpacing:'-0.01em', fontWeight: 500}}>{t('auth.wizard.title')}</h1>
            <p style={{color:'var(--fg-3)', fontSize: 13, margin:'0 0 18px'}}>
              {(() => {
                const parts = t('auth.wizard.body').split('{ONLY}');
                return <>{parts[0]}<b style={{color:'var(--fg-1)'}}>{t('auth.wizard.body.only')}</b>{parts[1] || ''}</>;
              })()}
            </p>

            <div className="card" style={{padding: 12, background:'rgba(230, 168, 90, 0.08)', borderColor:'rgba(230, 168, 90, 0.3)', marginBottom: 14}}>
              <div className="row gap-2" style={{alignItems:'flex-start'}}>
                <XIcons.IconShield size={13} style={{color:'var(--warn)', marginTop: 2}}/>
                <div style={{fontSize: 12.5, lineHeight: 1.7, color:'var(--fg-2)'}}>
                  {t('auth.wizard.warn')}
                </div>
              </div>
            </div>

            <div className="field-label">{t('auth.wizard.keyLabel')}</div>
            <div className="hairline mono" style={{padding:'14px 16px', borderRadius: 6, background:'var(--bg-2)', fontSize: 13, wordBreak:'break-all', marginBottom: 10}}>
              {wizard.apiKey}
            </div>

            <div className="row gap-2">
              <button className="btn grow" style={{justifyContent:'center'}} onClick={copyKey}>
                <XIcons.IconCopy size={13}/> {t('auth.wizard.copy')}
              </button>
              <button className="btn grow" style={{justifyContent:'center'}} onClick={downloadKey}>
                <XIcons.IconDownload size={13}/> {t('auth.wizard.download')}
              </button>
            </div>

            <div className="row gap-2" style={{marginTop: 10, alignItems:'center', fontSize: 12, color: wizard.saved ? 'var(--ok)' : 'var(--fg-4)'}}>
              {wizard.saved
                ? <><XIcons.IconCheck size={12}/> <span>{t('auth.wizard.saved')}</span></>
                : <><XIcons.IconInfo size={12}/> <span>{t('auth.wizard.gateHint')}</span></>}
            </div>

            {/* Primary: jump straight to Twitter OAuth (no intermediate card). */}
            <button
              className="btn btn-primary btn-lg"
              style={{width:'100%', justifyContent:'center', marginTop: 18, opacity: (!wizard.saved || wizard.busy) ? 0.5 : 1, cursor: (!wizard.saved || wizard.busy) ? 'not-allowed' : 'pointer'}}
              disabled={!wizard.saved || wizard.busy}
              onClick={continueWithXDirect}
            >
              {wizard.busy
                ? <><div className="spinner"/> {t('auth.wizard.continueX.busy')}</>
                : <><XIcons.IconTwitter size={14}/> {t('auth.wizard.continueX')} <XIcons.IconArrowRight size={13}/></>}
            </button>

            {/* Secondary: skip Twitter binding, stay on the virtual account. */}
            <button
              className="btn btn-ghost"
              style={{width:'100%', justifyContent:'center', height: 36, marginTop: 8}}
              disabled={wizard.busy}
              onClick={skipToConsole}
            >
              {t('auth.wizard.skip')} <XIcons.IconArrowRight size={12}/>
            </button>

            {wizard.err && <div style={{marginTop: 10, color:'var(--err)', fontSize: 12}}>{wizard.err}</div>}

            <p className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', textAlign:'center', marginTop: 14, lineHeight: 1.6}}>
              {t('auth.wizard.footnote')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:'calc(100vh - 52px)', display:'flex', alignItems:'center', justifyContent:'center', padding: 32}}>
      <div className="card" style={{padding: 36, width: 460, position:'relative', overflow:'hidden'}}>
        <div className="grid-bg" style={{position:'absolute', inset:0, opacity: 0.3}}/>
        <div style={{position:'relative'}}>
          <XPrim.Logo/>
          <h1 style={{margin:'20px 0 6px', fontSize: 22, letterSpacing:'-0.01em', fontWeight: 500}}>
            {t(forFreeApi ? 'apidetail.free.title' : 'auth.login.title')}
          </h1>
          <p style={{color:'var(--fg-3)', fontSize: 13, margin:'0 0 22px'}}>
            {t(forFreeApi ? 'apidetail.free.signin' : 'auth.login.subtitle')}
          </p>

          <div className="col gap-2">
            <button className="btn btn-lg" style={{width:'100%', justifyContent:'center'}}
              disabled={!!busy} onClick={useTwitter}>
              {busy === 'twitter' ? <><div className="spinner"/> {t('auth.login.twitter.busy')}</> : <><XIcons.IconTwitter size={14}/> {t('auth.login.twitter')}</>}
            </button>
          </div>

          <div className="row gap-3" style={{margin:'22px 0', alignItems:'center'}}>
            <div style={{flex: 1, height: 1, background:'var(--line)'}}/>
            <span className="micro">{t('auth.login.divider')}</span>
            <div style={{flex: 1, height: 1, background:'var(--line)'}}/>
          </div>

          <form onSubmit={submitKey}>
            <label className="field-label">{t('auth.login.apiKey.label')}</label>
            <input className="input" placeholder={t('auth.login.apiKey.placeholder')} value={apiKey}
              onChange={e=>{setApiKey(e.target.value); setErr(null);}}
              style={{marginBottom: 10}} disabled={!!busy}/>
            {err && <div style={{color:'var(--err)', fontSize: 12, marginBottom: 8}}>{err}</div>}
            <button type="submit" className="btn btn-primary" style={{width:'100%', justifyContent:'center'}} disabled={!!busy}>
              {busy === 'apikey' ? <><div className="spinner"/> {t('auth.login.apiKey.busy')}</> : <>{t('auth.login.apiKey.submit')} <XIcons.IconArrowRight size={13}/></>}
            </button>
          </form>

          <div style={{marginTop: 18, textAlign:'center'}}>
            {!showReferral ? (
              <button onClick={()=>setShowReferral(true)} className="micro-mono"
                style={{background:'none', border:'none', color:'var(--fg-4)', cursor:'pointer', textTransform:'none', letterSpacing:0}}>
                {t('auth.login.referral.add')}
              </button>
            ) : (
              <div>
                <label className="field-label" style={{marginTop: 0}}>{t('auth.login.referral.label')}</label>
                <input className="input" placeholder={t('auth.login.referral.placeholder')} value={referral}
                  onChange={e=>setReferral(e.target.value.trim())} disabled={!!busy}/>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const BaseBrandIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
    <circle cx="16" cy="16" r="16" fill="#0052FF" />
    <circle cx="16" cy="16" r="7.6" fill="none" stroke="#fff" strokeWidth="4.2" />
  </svg>
);

const BSC_U_TOKEN_ICON = '/tokens/u.jpg';
const BSC_STABLECOINS = [
  { key: 'U', label: 'U', method: 'EIP-3009', icon: BSC_U_TOKEN_ICON },
  { key: 'USD1', label: 'USD1', method: 'EIP-3009', icon: '/tokens/usd1.png' },
  { key: 'USDT', label: 'USDT', method: 'Permit2', icon: '/tokens/usdt.png' },
  { key: 'USDC', label: 'USDC', method: 'Permit2', icon: '/tokens/usdc.png' },
];

const TokenImageIcon = ({ src, size = 18 }) => (
  <img src={src} alt="" aria-hidden="true" width={size} height={size} style={{width: size, height: size, borderRadius: 999, display:'block'}}/>
);

const StablecoinIcon = ({ token, size = 18 }) => {
  if (token.icon) return <TokenImageIcon src={token.icon} size={size}/>;
  return (
    <span aria-hidden="true" className="mono" style={{
      width: size, height: size, borderRadius: 999, display:'inline-flex',
      alignItems:'center', justifyContent:'center', fontSize: Math.max(9, Math.round(size * 0.48)),
      background:'var(--bg-3)', color:'var(--fg-1)', border:'1px solid var(--line)',
    }}>{token.label[0]}</span>
  );
};

const TokenIconStack = ({ tokens, size = 18, max = 4 }) => {
  const visible = (tokens || []).slice(0, max);
  const overlap = Math.round(size * 0.36);
  const width = visible.length ? size + (visible.length - 1) * (size - overlap) : size;

  return (
    <span role="img" aria-label={visible.map(token => token.label).join(', ')} style={{
      width, height: size, display:'inline-flex', alignItems:'center', position:'relative',
    }}>
      {visible.map((token, index) => (
        <span key={token.key} style={{
          width: size, height: size, borderRadius: 999, display:'inline-flex',
          alignItems:'center', justifyContent:'center', position:'absolute',
          left: index * (size - overlap), zIndex: visible.length - index,
          background:'var(--bg-2)', boxShadow:'0 0 0 2px var(--bg-3)',
        }}>
          <StablecoinIcon token={token} size={size}/>
        </span>
      ))}
    </span>
  );
};

const PaymentMethodIcon = ({ icon, icons }) => {
  const isStack = Array.isArray(icons) && icons.length > 1;
  return (
    <div style={{
      width: isStack ? 54 : 32, height: 32, background:'var(--bg-3)', borderRadius: 6,
      display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0,
    }}>
      {isStack ? <TokenIconStack tokens={icons} size={18}/> : icon}
    </div>
  );
};

// ============ TopUp ============
const stripeTopupDraftKey = ownerId => `xapi.stripe.topup-draft.${ownerId}`;

const TopUpPage = ({ embedded = false, useCrypto: controlledCrypto, onCryptoChange, onBusyChange }) => {
  const { navigate, route } = XShell.useRoute();
  const { t } = useT();
  const { authed, user, refreshMe, setUser } = XShell.useAuth();
  const ownerId = user?._raw?.id || user?.id || null;
  const { trackPayment } = useStripePaymentTracking();
  const { toast } = XPrim.useToast();
  const [amt, setAmt] = uxS(100);
  const [localCrypto, setLocalCrypto] = uxS(false);
  const useCrypto = controlledCrypto ?? localCrypto;
  const setUseCrypto = onCryptoChange || setLocalCrypto;
  const [cryptoMethod, setCryptoMethod] = uxS('x402');
  const method = useCrypto ? cryptoMethod : 'stripe';
  const [bscAsset, setBscAsset] = uxS('U');
  const [busy, setBusy] = uxS(false);
  const [err, setErr] = uxS(null);
  const topupRootRef = React.useRef(null);
  const restoredDraftOwner = React.useRef(null);

  // Only restore into the payment dialog. The intermediate callback page must
  // leave the draft available for the dialog opened after a cancellation.
  React.useEffect(() => {
    if (embedded && ownerId && restoredDraftOwner.current === ownerId) return;
    restoredDraftOwner.current = ownerId;
    setAmt(100);
    setCryptoMethod('x402');
    setLocalCrypto(false);
    if (!embedded || !ownerId) return;
    try {
      const key = stripeTopupDraftKey(ownerId);
      const draft = JSON.parse(sessionStorage.getItem(key) || 'null');
      sessionStorage.removeItem(key);
      if (draft?.method === 'stripe' && Number.isFinite(draft.amountUsd) &&
          draft.amountUsd >= 1 && draft.amountUsd <= 100000 &&
          Math.abs(draft.amountUsd * 100 - Math.round(draft.amountUsd * 100)) < 1e-6) {
        setAmt(draft.amountUsd);
        setUseCrypto(false);
      }
    } catch { /* A browser storage restriction must not block a top-up. */ }
  }, [embedded, ownerId]);

  // Stripe return-flow: route.params.stripe = 'success'|'cancel' (seeded by api/bootstrap.js).
  // Verify ownership and status before handing a pending payment to the global tracker.
  // The tracker survives navigation and refreshes the shared account balance on settlement.
  const stripeFlag = route.params && route.params.stripe;
  const stripeSession = route.params && route.params.session;
  const [verifying, setVerifying] = uxS(false);
  React.useEffect(() => {
    if (stripeFlag === 'success' && stripeSession && authed) {
      let cancelled = false;
      setVerifying(true);
      const startedAt = Date.now();
      const POLL_MS = 2000, MAX_MS = 30_000;
      (async () => {
        let last = null;
        while (!cancelled && Date.now() - startedAt < MAX_MS) {
          try {
            last = await XApi.stripeService.getSession(stripeSession);
            if (last && !last.pending && [
              'complete', 'reversed', 'partially_reversed', 'expired',
            ].includes(last.status)) break;
            if (last?.status === 'processing' || last?.pending) break;
            // Allow a short Stripe status transition after Checkout redirect,
            // then stop waiting when the Session is still definitively unpaid.
            if (last?.status === 'awaiting_payment' && Date.now() - startedAt >= 4_000) break;
          } catch (e) { last = { error: e.message }; }
          await new Promise(r => setTimeout(r, POLL_MS));
        }
        if (cancelled) return;
        try { sessionStorage.removeItem(stripeTopupDraftKey(ownerId)); } catch {}
        if (last?.status === 'processing' || last?.pending) trackPayment(stripeSession);
        if (last && last.status === 'complete' && !last.pending) {
          toast(`Payment confirmed: $${Number(last.creditedUsd || last.amountUsd || 0).toFixed(2)}`);
        } else if (last && last.status === 'reversed') {
          toast('Payment was reversed; the top-up credit has been removed');
        } else if (last && last.status === 'partially_reversed') {
          toast(`Payment partially reversed: $${Number(last.creditedUsd || 0).toFixed(2)} remains credited`);
        } else if (last && last.error) {
          toast('Payment status check failed: ' + last.error);
        } else if (last && last.status === 'unknown') {
          toast('Could not verify payment status; check your billing history');
        } else if (last && last.status === 'expired') {
          toast('Payment session expired; no credit was added');
        } else if (last && last.status === 'awaiting_payment') {
          toast('Payment has not been confirmed; check your billing history');
        } else if (last && last.status === 'processing') {
          toast('Payment is processing; billing will update after confirmation');
        } else if (last && last.pending) {
          toast('Payment confirmed by Stripe; credit is still being processed');
        } else {
          toast('Could not verify payment status; check your billing history');
        }
        // Always refresh /auth/me so BAlance / TopBar reflect the latest server state.
        try { await refreshMe(); } catch {}
        if (cancelled) return;
        setVerifying(false);
        navigate('console', { tab: 'billing' });
      })();
      return () => { cancelled = true; };
    } else if (stripeFlag === 'cancel') {
      toast('Payment canceled');
      navigate('console', { tab: 'billing', payment: 'manual' });
    }
  }, [stripeFlag, stripeSession, authed, ownerId, trackPayment]);

  const wallet = XWallet ? XWallet.useWallet() : { address: null, hasProvider: false, connectors: [] };
  const [pickerOpen, setPickerOpen] = uxS(false);
  const locked = busy || verifying || (useCrypto && Boolean(wallet.connecting));
  React.useEffect(() => {
    onBusyChange?.(locked);
    // Browser back can unmount the dialog without going through its Close button.
    return () => { onBusyChange?.(false); };
  }, [locked, onBusyChange]);
  React.useEffect(() => { setErr(null); if (!useCrypto) setPickerOpen(false); }, [useCrypto]);

  const connectWith = async (entry) => {
    setErr(null);
    try {
      await wallet.connect(entry);
      setPickerOpen(false);
    } catch (e) {
      // wagmi throws ConnectorAlreadyConnectedError if user clicks the same connector again
      // while a connection is in flight — ignore that silently, surface other errors.
      if (!/already.*connected/i.test(e.message || '')) setErr(e.message || 'Wallet connect failed');
    }
  };

  const handleTopUp = async () => {
    if (!authed) { toast('Sign in first'); navigate('login'); return; }
    if (!Number.isFinite(amt) || amt < (method === 'stripe' ? 1 : 0.01)) {
      setErr(`Amount must be ≥ $${method === 'stripe' ? '1.00' : '0.01'}`); return;
    }
    if (method === 'stripe' && (amt > 100000 || Math.abs(amt * 100 - Math.round(amt * 100)) > 1e-6)) {
      setErr('Card top-ups must be at most $100,000.00 and use no more than two decimal places'); return;
    }
    setBusy(true); setErr(null);
    try {
      if (method === 'stripe') {
        const s = await XApi.stripeService.createSession(amt);
        const url = s && (s.url || s.sessionUrl);
        if (!url) throw new Error('Stripe did not return a checkout URL');
        try {
          if (ownerId) sessionStorage.setItem(stripeTopupDraftKey(ownerId), JSON.stringify({ amountUsd: amt, method: 'stripe' }));
        } catch { /* Continue checkout when sessionStorage is unavailable. */ }
        window.location.href = url;
      } else if (method === 'x402' || method === 'bsc-u') {
        if (!wallet.hasProvider) throw new Error('Install MetaMask or Coinbase Wallet to pay on-chain.');
        if (!wallet.address) await wallet.connect();
        const r = await XApi.x402Service.checkout(amt, {
          provider: method === 'bsc-u' ? 'b402' : 'x402',
          asset: method === 'bsc-u' ? bscAsset : undefined,
        });
        if (r.status >= 400) {
          const msg = (r.data && (r.data.error || r.data.message)) || ('HTTP ' + r.status);
          throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
        }
        const credited = (r.data && (r.data.creditedUsd || r.data.creditedUSD || r.data.amount)) || amt;
        const currentBalance = Number(r.data && (r.data.currentBalance ?? r.data.balance));
        const tx = (r.data && (r.data.txHash || r.data.transactionHash)) || (r.receipt && (r.receipt.transactionHash || r.receipt.txHash));
        toast(`Payment confirmed: $${Number(credited).toFixed(2)}`);
        if (tx) console.log('x402 tx:', tx);
        if (Number.isFinite(currentBalance)) {
          setUser((prev) => prev ? ({
            ...prev,
            balance: currentBalance,
            _raw: prev._raw ? { ...prev._raw, balance: currentBalance } : prev._raw,
          }) : prev);
        }
        // Refresh user so TopBar / sidebar / Overview reflect the new balance.
        try { await refreshMe(); } catch {}
        navigate('console', { tab: 'billing', refresh: Date.now() });
      }
    } catch (e) {
      setErr(e.message || 'Top-up failed');
    } finally { setBusy(false); }
  };

  if (!embedded && (stripeFlag === 'cancel' || (stripeFlag === 'success' && stripeSession))) {
    return <div role="status" style={{ padding: 40, color: 'var(--fg-3)' }}>
      {stripeFlag === 'success' ? t('topup.verifying') : t('common.loading')}
    </div>;
  }

  return (
    <div ref={topupRootRef} className={`topup-shell${embedded ? ' topup-shell-embedded' : ''}`} style={{maxWidth: 900, margin:'0 auto', padding: embedded ? 0 : '40px clamp(14px, 5vw, 32px) 80px'}}>
      {!embedded && <><div className="row gap-2" style={{color:'var(--fg-3)', fontSize: 12, marginBottom: 14}}>
        <a onClick={()=>navigate('console', { tab: 'billing' })} style={{cursor:'pointer'}}>{t('console.billing.title')}</a>
        <XIcons.IconChevronRight size={11}/>
        <span style={{color:'var(--fg-1)'}}>Top up</span>
      </div>
      <div className="row between gap-3"><h1 style={{margin:0, fontSize: 28, letterSpacing:'-0.02em', fontWeight: 500}}>Add funds</h1>
        <RechargeModeSwitch checked={useCrypto} onChange={setUseCrypto} disabled={locked} /></div></>}

      <div className="card" style={{padding: 28, marginTop: embedded ? 0 : 24}}>
        <div className="micro">{t('topup.amount')}</div>
        <div className="row gap-3" style={{marginTop: 10, alignItems:'baseline'}}>
          <span className="mono" style={{fontSize: 40, letterSpacing:'-0.02em'}}>$</span>
          <input type="number" min={method === 'stripe' ? '1' : '0.01'} max={method === 'stripe' ? '100000' : undefined} step="0.01" aria-label={t('topup.amount')} disabled={locked} value={amt} onChange={e=>setAmt(+e.target.value)} className="mono" style={{flex: 1, fontSize: 40, background:'transparent', border:'none', outline:'none', color:'var(--fg-0)', letterSpacing:'-0.02em'}}/>
        </div>
        <div className="row gap-2" style={{marginTop: 14, flexWrap:'wrap'}}>
          {[10, 50, 100, 500, 1000].map(v => (
            <button key={v} className={`btn btn-sm ${amt === v ? 'btn-primary' : ''}`} disabled={locked} onClick={()=>setAmt(v)}>${v}</button>
          ))}
        </div>
      </div>

      <div className="topup-payment-options">
      {useCrypto ? <><div className="micro" style={{margin:'24px 0 10px'}}>{t('topup.cryptoNetwork')}</div>
      <div className="col gap-2">
        {[
          {k:'x402', t:'Base USDC', d:t('topup.baseHint'), icon: <BaseBrandIcon size={18}/>, badge:'x402'},
          {k:'bsc-u', t:t('topup.bsc'), d:t('topup.bscHint'), icons: BSC_STABLECOINS, badge:'b402'},
        ].map(o => (
          <label key={o.k} onClick={()=>!locked && setCryptoMethod(o.k)} className="card card-h row gap-3" style={{padding: 16, cursor: locked ? 'wait' : 'pointer', borderColor: method === o.k ? 'var(--fg-0)' : 'var(--line)', opacity: locked ? 0.58 : 1}}>
            <input type="radio" checked={method === o.k} disabled={locked} name="topup-network" aria-label={o.t} onChange={()=>setCryptoMethod(o.k)} style={{accentColor:'#fff'}}/>
            <PaymentMethodIcon icon={o.icon} icons={o.icons}/>
            <div className="grow">
              <div className="row gap-2">
                <span style={{fontSize: 14}}>{o.t}</span>
                {o.badge && <span className="tag" style={{fontSize: 10, color: o.disabled ? 'var(--warn)' : undefined}}>{o.badge}</span>}
              </div>
              <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0}}>{o.d}</div>
            </div>
          </label>
        ))}
      </div>

      </> : <div className="topup-card-payment"><XIcons.IconWallet size={18}/><div><strong>{t('topup.card')}</strong><span>{t('topup.cardMethods')}</span></div><span className="tag">Stripe</span></div>}
      </div>

      {method === 'bsc-u' && (
        <div className="card" style={{padding: 14, marginTop: 14, background:'var(--bg-2)'}}>
          <div className="micro" style={{marginBottom: 10}}>{t('topup.bscToken')}</div>
          <div className="row gap-2" style={{flexWrap:'wrap'}}>
            {BSC_STABLECOINS.map(token => (
              <button key={token.key} className={`btn btn-sm ${bscAsset === token.key ? 'btn-primary' : ''}`}
                disabled={locked} onClick={()=>setBscAsset(token.key)} style={{gap: 8}}>
                <StablecoinIcon token={token} size={16}/>
                <span>{token.label}</span>
                <span className="micro-mono" style={{letterSpacing: 0, textTransform:'none', color: bscAsset === token.key ? 'inherit' : 'var(--fg-3)'}}>{token.method}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{padding: 18, marginTop: 20, background:'var(--bg-2)'}}>
        <div className="row between">
          <span className="micro">{t('topup.youPay')}</span>
          <span className="mono">${Number(amt || 0).toFixed(2)}</span>
        </div>
        <div className="row between" style={{marginTop: 8}}>
          <span className="micro">{t('topup.credited')}</span>
          <span className="mono" style={{color:'var(--ok)'}}>+${Number(amt || 0).toFixed(2)}</span>
        </div>
      </div>

      {(method === 'x402' || method === 'bsc-u') && (
        <div className="card" style={{padding: 14, marginTop: 14, background:'var(--bg-2)'}}>
          <div className="row between">
            <div>
              <div className="micro">{t('topup.wallet')} · {method === 'bsc-u' ? `BSC ${bscAsset}` : 'Base USDC'}</div>
              {wallet.address ? (
                <div className="mono" style={{marginTop: 4, fontSize: 12}}>
                  {wallet.address.slice(0, 8)}…{wallet.address.slice(-6)}
                  {wallet.chainId && <span style={{marginLeft: 10, color:'var(--fg-3)'}}>chain {wallet.chainId}</span>}
                </div>
              ) : (
                <div style={{marginTop: 4, fontSize: 12, color:'var(--fg-3)'}}>{t('topup.notConnected')}</div>
              )}
            </div>
            {wallet.address
              ? <button className="btn btn-sm" disabled={locked} onClick={()=>wallet.disconnect()}>{t('topup.disconnect')}</button>
              : <button className="btn btn-sm btn-primary" onClick={()=>setPickerOpen(true)} disabled={wallet.connecting}>
                  {wallet.connecting ? <><div className="spinner"/> {t('topup.connecting')}</> : <><XIcons.IconWallet size={11}/> {t('topup.connectWallet')}</>}
                </button>}
          </div>
        </div>
      )}

      {/* Wallet picker — centered modal on desktop, bottom drawer on mobile.
          Layout switching handled by `wallet-picker-overlay/panel` CSS classes. */}
      {pickerOpen && createPortal(
        <div onClick={()=>setPickerOpen(false)} className="wallet-picker-overlay">
          <div onClick={e=>e.stopPropagation()} className="wallet-picker-panel">
            <div className="wallet-picker-handle" aria-hidden/>
            <div className="row between" style={{padding:'14px 18px', borderBottom:'1px solid var(--line)'}}>
              <div>
                <div className="micro">{t('topup.connectWallet')}</div>
                <div style={{marginTop: 4, fontSize: 13, color:'var(--fg-3)'}}>{t('topup.walletHint')}</div>
              </div>
              <button className="btn btn-sm btn-ghost btn-icon" onClick={()=>setPickerOpen(false)}>
                <XIcons.IconX size={12}/>
              </button>
            </div>
            <div className="col wallet-picker-list" style={{padding: 12, gap: 6}}>
              {(wallet.connectors || []).map(c => (
                <button key={c.id} onClick={()=>connectWith(c)} disabled={wallet.connecting}
                  className="card card-h row gap-3" style={{
                    padding: 12, cursor: wallet.connecting ? 'wait' : 'pointer',
                    background:'var(--bg-2)', border:'1px solid var(--line)', borderRadius: 6,
                  }}>
                  <div style={{
                    width: 32, height: 32, background:'var(--bg-3)', borderRadius: 6,
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0,
                    overflow:'hidden',
                  }}>
                    {c.icon
                      ? <img src={c.icon} alt="" style={{width: 24, height: 24, objectFit:'contain'}}/>
                      : <XIcons.IconWallet size={14}/>}
                  </div>
                  <div style={{flex: 1, minWidth: 0, textAlign:'left'}}>
                    <div style={{fontSize: 13, color:'var(--fg-1)'}}>{c.label || c.name}</div>
                    {c.hint && <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, marginTop: 2}}>{c.hint}</div>}
                  </div>
                  <XIcons.IconArrowRight size={12} style={{color:'var(--fg-3)'}}/>
                </button>
              ))}
              {(!wallet.connectors || wallet.connectors.length === 0) && (
                <div style={{padding:'24px 16px', textAlign:'center', fontSize: 13, color:'var(--fg-3)', lineHeight: 1.6}}>
                  {t('topup.noWallet')} <a href="https://metamask.io" target="_blank" rel="noreferrer" style={{color:'var(--fg-1)', textDecoration:'underline'}}>MetaMask</a> · <a href="https://www.okx.com/web3" target="_blank" rel="noreferrer" style={{color:'var(--fg-1)', textDecoration:'underline'}}>OKX</a> · <a href="https://www.coinbase.com/wallet" target="_blank" rel="noreferrer" style={{color:'var(--fg-1)', textDecoration:'underline'}}>Coinbase Wallet</a>
                </div>
              )}
            </div>
            {wallet.connectError && (
              <div style={{padding:'10px 18px', background:'rgba(239,68,68,0.08)', borderTop:'1px solid rgba(239,68,68,0.3)', color:'var(--err)', fontSize: 12}}>
                {wallet.connectError}
              </div>
            )}
          </div>
        </div>,
        embedded ? topupRootRef.current : document.body
      )}
      {err && <div style={{color:'var(--err)', fontSize: 12, marginTop: 12}}>{err}</div>}
      {verifying && (
        <div className="card" style={{padding: 14, marginTop: 12, background:'var(--bg-2)'}}>
          <div className="row gap-2" style={{alignItems:'center'}}>
            <div className="spinner"/>
            <span style={{fontSize: 13, color:'var(--fg-2)'}}>{t('topup.verifying')}</span>
          </div>
        </div>
      )}
      <button className="btn btn-primary btn-lg" style={{width:'100%', justifyContent:'center', marginTop: 20}}
        disabled={locked} onClick={(method === 'x402' || method === 'bsc-u') && !wallet.address ? () => setPickerOpen(true) : handleTopUp}>
        {busy
          ? <><div className="spinner"/> {t((method === 'x402' || method === 'bsc-u') ? 'topup.awaitingWallet' : 'topup.openingCheckout')}</>
          : (method === 'x402' || method === 'bsc-u')
            ? <>{wallet.address ? t('topup.payWith', { asset: method === 'bsc-u' ? bscAsset : 'USDC' }) : t('topup.connectToPay')} <XIcons.IconArrowRight size={14}/></>
            : <>{t('topup.continue')} <XIcons.IconArrowRight size={14}/></>}
      </button>
    </div>
  );
};

// ============ Capability ============
// Capabilities = cross-API skills. Each capability exposes a single unified input shape
// and may route to 1+ upstream APIs. This page lets you test-call a capability live
// using your XAPI_KEY from the Environment tab — same wire as Try-It on api detail.

const CAPABILITIES = [
  {
    id: 'web-research', name: 'Web research', icon: 'IconGlobe',
    desc: 'Search the open web, fetch pages, and return structured snippets.',
    apis: ['search.xapi', 'scrape.xapi', 'llm-gateway'],
    route: 'POST /v1/capabilities/web_research',
    inputs: [
      { key: 'query', type: 'string', required: true, placeholder: 'Latest MCP spec updates', desc: 'Natural-language query' },
      { key: 'max_results', type: 'number', default: 5, desc: '1–20' },
      { key: 'summarize', type: 'boolean', default: true, desc: 'Return LLM summary in addition to snippets' },
    ],
    sample: (v) => ({
      query: v.query || 'Latest MCP spec updates',
      results: [
        { url: 'https://modelcontextprotocol.io/spec', title: 'Model Context Protocol — Specification', snippet: 'MCP 1.1 introduces streaming tool responses and…' },
        { url: 'https://github.com/anthropics/mcp', title: 'anthropics/mcp · GitHub', snippet: 'Reference servers and SDKs for MCP…' },
        { url: 'https://blog.xapi.to/mcp-update', title: 'xapi × MCP · Release notes', snippet: 'All 47 marketplace APIs now exposed via /sse…' },
      ].slice(0, v.max_results || 3),
      summary: v.summarize !== false ? 'MCP 1.1 adds streaming; xapi gateway supports it out of the box.' : undefined,
      usage: { apis_called: 3, tokens: 842, cost_usd: 0.0041 },
    }),
  },
  {
    id: 'code-exec', name: 'Code execution', icon: 'IconTerminal',
    desc: 'Run sandboxed Python or JS; returns stdout, stderr, and artifacts.',
    apis: ['sandbox.xapi'],
    route: 'POST /v1/capabilities/code_exec',
    inputs: [
      { key: 'language', type: 'enum', options: ['python', 'node'], default: 'python' },
      { key: 'source', type: 'text', required: true, placeholder: 'print(sum(range(100)))', rows: 5 },
      { key: 'timeout_ms', type: 'number', default: 2000 },
    ],
    sample: (v) => ({
      language: v.language || 'python',
      stdout: v.source && v.source.includes('sum') ? '4950\n' : 'hello, world\n',
      stderr: '',
      exit_code: 0,
      duration_ms: 184,
      usage: { cost_usd: 0.0002 },
    }),
  },
  {
    id: 'doc-parse', name: 'Document parse', icon: 'IconBook',
    desc: 'PDF, DOCX, or image URL → structured text + tables.',
    apis: ['parse.xapi', 'ocr.xapi'],
    route: 'POST /v1/capabilities/doc_parse',
    inputs: [
      { key: 'url', type: 'string', required: true, placeholder: 'https://example.com/whitepaper.pdf' },
      { key: 'extract', type: 'multi', options: ['text', 'tables', 'headings', 'images'], default: ['text', 'tables'] },
    ],
    sample: (v) => ({
      url: v.url || 'https://example.com/whitepaper.pdf',
      pages: 12,
      text: '# xapi whitepaper\n\nOne endpoint for every API…',
      tables: (v.extract || []).includes('tables') ? [{ page: 4, rows: 18, cols: 4 }] : undefined,
      usage: { cost_usd: 0.012 },
    }),
  },
  {
    id: 'messaging', name: 'Messaging', icon: 'IconBell',
    desc: 'Send SMS, email, or trigger webhooks through a single adapter.',
    apis: ['sms.xapi', 'email.xapi', 'webhook.xapi'],
    route: 'POST /v1/capabilities/send_message',
    inputs: [
      { key: 'channel', type: 'enum', options: ['sms', 'email', 'webhook'], default: 'email', required: true },
      { key: 'to', type: 'string', required: true, placeholder: 'user@example.com' },
      { key: 'body', type: 'text', required: true, rows: 3, placeholder: 'Your order #1234 has shipped.' },
    ],
    sample: (v) => ({
      channel: v.channel || 'email',
      message_id: 'msg_' + Math.random().toString(36).slice(2, 10),
      status: 'queued',
      to: v.to || 'user@example.com',
      usage: { cost_usd: 0.001 },
    }),
  },
  {
    id: 'payments', name: 'Payments', icon: 'IconDollar',
    desc: 'Charge cards, accept x402, Solana Pay — unified receipt shape.',
    apis: ['stripe.xapi', 'x402.xapi', 'solana-pay.xapi'],
    route: 'POST /v1/capabilities/charge',
    inputs: [
      { key: 'rail', type: 'enum', options: ['card', 'x402', 'solana'], default: 'card' },
      { key: 'amount_usd', type: 'number', required: true, default: 5.00 },
      { key: 'reference', type: 'string', placeholder: 'order-1234' },
    ],
    sample: (v) => ({
      rail: v.rail || 'card', status: 'succeeded',
      charge_id: 'ch_' + Math.random().toString(36).slice(2, 10),
      amount_usd: v.amount_usd || 5.00,
      usage: { cost_usd: 0.005 },
    }),
  },
  {
    id: 'identity', name: 'Identity', icon: 'IconShield',
    desc: 'KYC, age, sanctions screen — returns a signed verdict token.',
    apis: ['kyc.xapi', 'age.xapi'],
    route: 'POST /v1/capabilities/verify',
    inputs: [
      { key: 'check', type: 'enum', options: ['age', 'kyc', 'sanctions'], default: 'age' },
      { key: 'subject_id', type: 'string', required: true, placeholder: 'usr_abc123' },
    ],
    sample: (v) => ({
      check: v.check || 'age', verdict: 'pass',
      verdict_token: 'vrd_' + Math.random().toString(36).slice(2, 16),
      subject_id: v.subject_id || 'usr_abc123',
      usage: { cost_usd: 0.02 },
    }),
  },
];

// Build an empty value matching a JSON-schema node — used to seed a readable
// template for complex (array/object) fields so the user edits a shape, not a
// blank box.
const emptyVal = (p) => {
  if (!p || typeof p !== 'object') return '';
  switch (p.type) {
    case 'object': {
      const o = {};
      Object.entries(p.properties || {}).forEach(([k, sp]) => { o[k] = emptyVal(sp); });
      return o;
    }
    case 'array':
      return p.items ? [emptyVal(p.items)] : [];
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return false;
    default:
      return '';
  }
};

// Pretty-printed JSON skeleton for a complex field, e.g. messages →
// [ { "role": "", "content": "" } ]. Falls back to [] / {}.
const jsonTemplate = (p) => {
  try { return JSON.stringify(emptyVal(p), null, 2); }
  catch { return p.type === 'object' ? '{}' : '[]'; }
};

// Map JSON-schema property → CapField shape used by the existing UI.
const schemaToInputs = (schema) => {
  if (!schema || typeof schema !== 'object') return [];
  const props = schema.properties || {};
  const required = new Set(schema.required || []);
  return Object.entries(props).map(([key, p]) => {
    const t = p.type || 'string';
    if (Array.isArray(p.enum) && p.enum.length) {
      return { key, type: 'enum', options: p.enum, default: p.default, required: required.has(key), desc: p.description };
    }
    if (t === 'array' && p.items && Array.isArray(p.items.enum)) {
      return { key, type: 'multi', options: p.items.enum, default: p.default, required: required.has(key), desc: p.description };
    }
    if (t === 'boolean') return { key, type: 'boolean', default: !!p.default, required: required.has(key), desc: p.description };
    if (t === 'number' || t === 'integer') return { key, type: 'number', default: p.default, required: required.has(key), desc: p.description, placeholder: p.example };
    // 复杂类型（对象 / 对象数组等）走 JSON 编辑器：值以原始字符串存于 draft，
    // 发送 / 预览时 JSON.parse。
    if (t === 'array' || t === 'object') {
      // 仅当后端 schema 给了 default/example 时才作为真实预填值（如 messages 的 example）。
      // 没有示例的可选复杂字段不预填——否则空骨架 {}/[] 会被当作用户输入发给上游，
      // 上游对 tool_choice:{}（缺 type）、tools:[] 等会反序列化报错。此时骨架只作占位
      // 提示（hint，不进 draft），用户没填就不发送。
      const seed = p.default != null ? p.default : (p.example != null ? p.example : null);
      if (seed != null) {
        return { key, type: 'json', default: JSON.stringify(seed, null, 2), required: required.has(key), desc: p.description };
      }
      return { key, type: 'json', hint: jsonTemplate(p), required: required.has(key), desc: p.description };
    }
    // 仅对字符串字段套用「长文本→多行」启发式。
    const long = (p.format === 'textarea') || (p.maxLength && p.maxLength > 200) || /(text|content|prompt|body|message)/i.test(key);
    return { key, type: long ? 'text' : 'string', default: p.default, required: required.has(key), desc: p.description, placeholder: p.example || p.default };
  });
};

// Materialize the request `input` from draft values, JSON-parsing `json`-typed
// fields. Returns { input, error } — error is the first JSON parse failure.
const buildCapabilityInput = (cap, draft) => {
  const out = {};
  let error = null;
  (cap?.inputs || []).forEach((f) => {
    let v = draft ? draft[f.key] : undefined;
    if (v == null || v === '') {
      if (f.default != null && f.type !== 'json') out[f.key] = f.default;
      return;
    }
    if (f.type === 'json' && typeof v === 'string') {
      try { v = JSON.parse(v); }
      catch (e) { if (!error) error = `${f.key}: JSON 无效 — ${e.message}`; out[f.key] = draft[f.key]; return; }
    }
    out[f.key] = v;
  });
  return { input: out, error };
};

// Capability sidebar groups are derived from the action id-prefix (twitter / web /
// ai / crypto / …). To keep neighboring groups visually distinct, we pick from a
// fixed palette of 6 max-distance hues rather than hashing into the full 360°
// circle (which clustered short prefixes like "ai" / "web" / "twitter" within
// ~30° of each other). Stable hash → palette index keeps the assignment
// deterministic across renders and immune to new categories appearing.
const CAP_PALETTE = [
  { hue: 165, chroma: 0.16 }, // mint  — green
  { hue:  35, chroma: 0.18 }, // amber — orange
  { hue: 290, chroma: 0.18 }, // violet — purple
  { hue: 220, chroma: 0.16 }, // sky   — blue
  { hue:  10, chroma: 0.18 }, // rose  — red
  { hue: 320, chroma: 0.16 }, // pink  — magenta
];

const hashIndex = (s, mod) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return ((h % mod) + mod) % mod;
};

const capCategoryMeta = (category) => {
  if (!category) {
    return { fg: 'var(--fg-3)', bg: 'var(--bg-3)', border: 'var(--line)', dot: 'var(--fg-4)', hue: null };
  }
  const slot = CAP_PALETTE[hashIndex(category, CAP_PALETTE.length)];
  const { hue, chroma } = slot;
  return {
    fg:     `oklch(0.82 ${chroma * 0.7} ${hue})`,
    bg:     `color-mix(in oklab, oklch(0.72 ${chroma} ${hue}) 16%, transparent)`,
    border: `color-mix(in oklab, oklch(0.72 ${chroma} ${hue}) 44%, var(--line))`,
    dot:    `oklch(0.72 ${chroma} ${hue})`,
    hue,
  };
};

// Best-effort capability adapter from McpToolSummary / McpTool to the existing UI shape.
//
// Backend McpTool (apps/action/src/types/mcp.types.ts:47) uses `input` (JSONSchema) for the
// parameter spec — NOT `inputSchema` — and `meta.{title,description}` instead of top-level
// title/desc. List endpoint returns the summary form (no input/output), so input only shows up
// after detail() resolves. We try every plausible field name to remain forward-compatible.
const adaptCapability = (raw) => {
  if (!raw) return null;
  const id = raw.id || raw.actionId || raw.tool_id || raw.name;
  const name = raw.displayName || raw.meta?.title || raw.title || raw.name || id;
  const desc = raw.meta?.description || raw.description || raw.summary || '';
  const apis = raw.upstreams || raw.apis || raw.services || (raw.serviceId ? [raw.serviceId] : []);
  const route = 'POST /v1/actions/execute';
  const schema = raw.input || raw.inputSchema || raw.input_schema || raw.parameters || raw.schema;
  const inputs = schemaToInputs(schema);
  const supportsStreaming = !!(raw.endpoint?.supportsStreaming || raw.streaming || raw.supportsStreaming || raw.stream);
  const category = raw.meta?.category || raw.category || null;
  // Prefix derived from the action id — e.g. "twitter.user_tweets" → "twitter".
  // We bucket the sidebar by this prefix because backend ids are namespaced and the
  // namespace is the most reliable signal of "what kind of capability is this".
  const idPrefix = (id || '').split(/[._\/\-:]/)[0] || 'other';
  return { id, name, desc, apis: apis.length ? apis : ['xapi-actions'], route, inputs, icon: 'IconBlocks', _raw: raw, supportsStreaming, category, idPrefix };
};

const CapabilityPage = () => {
  const { useState: ucS, useMemo: ucM } = React;
  const { navigate } = XShell.useRoute();
  const { t } = useT();
  const envVars = XEnv.useEnv();
  const apiKeyVar = envVars.find(v => v.name === 'XAPI_KEY');
  const hasKey = !!(apiKeyVar && apiKeyVar.value);
  const keyPreview = hasKey ? (apiKeyVar.value.slice(0, 8) + '…' + apiKeyVar.value.slice(-4)) : null;

  const [list, setList] = ucS([]);
  const [listLoading, setListLoading] = ucS(true);
  const [listErr, setListErr] = ucS(null);
  const [selId, setSelId] = ucS(null);
  const [detailMap, setDetailMap] = ucS({}); // id → adapted capability with inputs
  // Sidebar groups (by id-prefix) start expanded. Toggling stores the prefix → collapsed=true.
  const [collapsed, setCollapsed] = ucS({});
  const toggleGroup = (prefix) => setCollapsed(m => ({ ...m, [prefix]: !m[prefix] }));
  // Mobile-only: sidebar opens as a bottom sheet on phones because the desktop
  // sticky sidebar doesn't fit alongside the detail card on narrow screens.
  const [mobilePickerOpen, setMobilePickerOpen] = ucS(false);

  React.useEffect(() => {
    if (!mobilePickerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobilePickerOpen]);

  React.useEffect(() => {
    let cancelled = false;
    setListLoading(true); setListErr(null);
    XApi.actionsService.list({ pageSize: 200 })
      .then(items => {
        if (cancelled) return;
        const adapted = items.map(adaptCapability).filter(Boolean);
        setList(adapted);
        setSelId(adapted[0] && adapted[0].id);
      })
      .catch(e => { if (!cancelled) setListErr(e.message || 'Could not load capabilities'); })
      .finally(() => { if (!cancelled) setListLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const summary = ucM(() => list.find(c => c.id === selId), [list, selId]);
  const cap = detailMap[selId] || summary;

  // Lazy-fetch full detail (inputSchema) on selection.
  React.useEffect(() => {
    if (!selId || detailMap[selId]) return;
    let cancelled = false;
    XApi.actionsService.detail(selId)
      .then(full => { if (!cancelled) setDetailMap(m => ({ ...m, [selId]: adaptCapability(full) })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selId]);

  // input state keyed by capability id so switching preserves drafts
  const [draftMap, setDraftMap] = ucS({});
  const draft = (cap && draftMap[cap.id]) || {};
  const setDraft = (patch) => setDraftMap(m => ({ ...m, [cap.id]: { ...(m[cap.id] || {}), ...patch } }));

  React.useEffect(() => {
    // 列表摘要不含 input（inputs 为空）；必须等详情加载出 inputs 后再 seed，
    // 否则会先写入一个空 draft，把后续真正的默认值挡在 seed-once 守卫之外，
    // 导致界面显示了默认值（CapField 回退 f.default）但 draft 实为空 → 校验失败。
    if (!cap || !(cap.inputs || []).length) return;
    setDraftMap(m => {
      const existing = m[cap.id] || {};
      const seed = { ...existing };
      let changed = false;
      (cap.inputs || []).forEach(f => {
        if (f.key in seed) return; // 保留用户已编辑/已清空的值
        if (f.default != null) { seed[f.key] = f.default; changed = true; }
        else if (f.placeholder) { seed[f.key] = f.placeholder; changed = true; }
      });
      return changed ? { ...m, [cap.id]: seed } : m;
    });
  }, [cap && cap.id, cap && cap.inputs && cap.inputs.length]);

  const [resp, setResp] = ucS(null);
  const [streamText, setStreamText] = ucS('');
  const [loading, setLoading] = ucS(false);
  const [error, setError] = ucS(null);
  const abortRef = React.useRef(null);
  React.useEffect(() => { setResp(null); setError(null); setStreamText(''); }, [selId]);

  const validate = () => {
    if (!cap) return 'Loading capability…';
    for (const f of cap.inputs || []) {
      if (f.required) {
        const v = draft[f.key];
        if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return f.key + ' is required';
      }
    }
    return null;
  };

  const send = async () => {
    if (!hasKey) { setError('XAPI_KEY is not set. Add it in the Environment tab.'); return; }
    const err = validate(); if (err) { setError(err); return; }
    const { input: filled, error: parseErr } = buildCapabilityInput(cap, draft);
    if (parseErr) { setError(parseErr); return; }
    setError(null); setLoading(true); setResp(null); setStreamText('');
    abortRef.current = new AbortController();
    const wantStream = !!cap.supportsStreaming || filled.stream === true;
    try {
      const r = await XApi.actionsService.execute({
        actionId: cap.id,
        input: filled,
        streaming: wantStream,
        signal: abortRef.current.signal,
        onChunk: wantStream ? ((p) => {
          // OpenAI/Anthropic SSE chunks: { choices:[{delta:{content:"..."}, ... }], ... }
          // Also handle simpler { content/text/message } envelopes and bare strings.
          let piece = '';
          if (typeof p === 'string') piece = p;
          else if (p) {
            const choice = p.choices && p.choices[0];
            const delta = choice && (choice.delta || choice.message);
            if (delta && typeof delta.content === 'string') piece = delta.content;
            else if (typeof p.content === 'string') piece = p.content;
            else if (typeof p.text === 'string') piece = p.text;
            else if (typeof p.message === 'string') piece = p.message;
          }
          if (piece) setStreamText(s => s + piece);
        }) : undefined,
      });
      setResp({ status: r.status || (r.ok ? 200 : 0), latency: r.latency, body: r.body, streaming: r.streaming });
    } catch (e) {
      if (e && (e.name === 'AbortError' || /aborted/i.test(e.message || ''))) setResp({ status: 0, latency: 0, body: null, error: 'Aborted' });
      else setError(e.message || 'Execute failed');
    } finally { setLoading(false); abortRef.current = null; }
  };

  const abort = () => { try { abortRef.current && abortRef.current.abort(); } catch {} };

  const reqJson = ucM(() => {
    const { input: out } = buildCapabilityInput(cap, draft);
    return JSON.stringify({ action_id: cap?.id, input: out }, null, 2);
  }, [draft, cap]);

  const Icon = XIcons[(cap && cap.icon) || 'IconBlocks'] || XIcons.IconBlocks;

  if (listLoading) {
    return <div style={{maxWidth: 1200, margin:'0 auto', padding:'80px 32px', textAlign:'center'}}>
      <div className="spinner" style={{width: 22, height: 22, margin:'0 auto'}}/>
      <div style={{marginTop: 14, color:'var(--fg-3)', fontSize: 13}}>{t('cap.loading')}</div>
    </div>;
  }
  if (listErr) {
    const url = `${XApi.config.capabilityBase}/v1/actions`;
    const ovr = 'VITE_CAPABILITY_URL';
    return <div style={{maxWidth: 720, margin:'0 auto', padding:'60px 32px'}}>
      <h1 style={{margin:0, fontSize: 24, fontWeight: 500}}>{t('cap.unavailable')}</h1>
      <p style={{color:'var(--fg-3)', fontSize: 13, marginTop: 10, lineHeight: 1.6}}>
        {(() => {
          const parts = t('cap.unavailableBody').split(/(\{URL\}|\{ERR\}|\{OVR\})/g);
          return parts.map((seg, i) => {
            if (seg === '{URL}') return <span key={i} className="mono">{url}</span>;
            if (seg === '{ERR}') return <span key={i}>{listErr}</span>;
            if (seg === '{OVR}') return <span key={i} className="mono">{ovr}</span>;
            return seg;
          });
        })()}
      </p>
    </div>;
  }
  if (!cap) {
    return <div style={{maxWidth: 720, margin:'0 auto', padding:'60px 32px', textAlign:'center', color:'var(--fg-3)'}}>{t('cap.empty')}</div>;
  }

  return (
    <div style={{maxWidth: 1480, margin:'0 auto', padding:'40px 40px 80px'}}>
      <div className="micro">{t('cap.eyebrow')}</div>
      <h1 style={{margin:'8px 0 0', fontSize: 28, letterSpacing:'-0.02em', fontWeight: 500}}>{t('cap.title')}</h1>
      <p style={{color:'var(--fg-3)', fontSize: 14, marginTop: 8, maxWidth: 640}}>
        {t('cap.subtitle')}
      </p>

      {/* Mobile-only picker trigger — opens the bottom drawer with the full
          group list. Desktop hides this button via .cap-mobile-trigger CSS.
          On mobile the sidebar itself is hidden (CSS) so this is the only way
          to switch capability. */}
      <button
        className="btn btn-sm cap-mobile-trigger"
        onClick={()=>setMobilePickerOpen(true)}
        style={{ marginTop: 18, width:'100%', justifyContent:'space-between' }}
      >
        <span className="row gap-2" style={{alignItems:'center', minWidth: 0}}>
          <XIcons.IconLayers size={12}/>
          <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{(cap && cap.name) || t('cap.skills')}</span>
        </span>
        <XIcons.IconChevronDown size={11}/>
      </button>

      <div className="cap-layout" style={{display:'grid', gridTemplateColumns:'260px 1fr', gap: 20, marginTop: 28, alignItems:'flex-start'}}>
        {/* Left: grouped list. Buckets are derived from the action-id prefix (the part
            before the first '.' or '_'), e.g. twitter.user_tweets → "twitter". Each group
            is collapsible. Sidebar is sticky + internally scrollable. Hidden on
            phones via CSS — replaced by the mobile picker drawer rendered below. */}
        <aside className="cap-sidebar" style={{
          position:'sticky', top: 80,
          maxHeight:'calc(100vh - 120px)',
          overflowY:'auto', overflowX:'hidden',
          paddingRight: 6,
          display:'flex', flexDirection:'column', gap: 4,
        }}>
          <div className="micro" style={{marginBottom: 8, position:'sticky', top: 0, background:'var(--bg-0)', paddingBottom: 4, zIndex: 1}}>
            {list.length} {t('cap.skills')}
          </div>
          {(() => {
            // Bucket by id-prefix.
            const buckets = new Map();
            for (const c of list) {
              const key = c.idPrefix || 'other';
              if (!buckets.has(key)) buckets.set(key, []);
              buckets.get(key).push(c);
            }
            // Order: by item count desc, then prefix asc.
            const groups = Array.from(buckets.entries())
              .map(([prefix, items]) => ({ prefix, items }))
              .sort((a, b) => {
                if (b.items.length !== a.items.length) return b.items.length - a.items.length;
                return a.prefix.localeCompare(b.prefix);
              });
            return groups.map(({ prefix, items }) => {
              const meta = capCategoryMeta(prefix);
              const isCollapsed = !!collapsed[prefix];
              const groupHasSel = items.some(c => c.id === selId);
              return (
                <div key={prefix} className="col gap-1" style={{marginBottom: 6}}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(prefix)}
                    className="row gap-2 cap-group-header"
                    aria-expanded={!isCollapsed}
                    style={{
                      alignItems:'center', padding:'8px 10px 6px',
                      borderTop: `1px dashed ${meta.border}`,
                      background:'transparent', border:'none',
                      borderTopLeftRadius: 0, borderTopRightRadius: 0,
                      cursor:'pointer', textAlign:'left', width:'100%',
                    }}>
                    <XIcons.IconChevronDown size={11} style={{
                      color: meta.hue != null ? meta.fg : 'var(--fg-3)',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform .12s',
                      flexShrink: 0,
                    }}/>
                    <span style={{width: 6, height: 6, borderRadius:'50%', background: meta.dot, boxShadow: meta.hue != null ? `0 0 8px ${meta.dot}` : 'none', flexShrink: 0}}/>
                    <span className="mono" style={{
                      fontSize: 10, letterSpacing: '0.08em', textTransform:'uppercase',
                      color: meta.hue != null ? meta.fg : 'var(--fg-3)',
                    }}>{prefix}</span>
                    {/* Hint when collapsed and the active selection lives inside this group */}
                    {isCollapsed && groupHasSel && (
                      <span className="mono" style={{fontSize: 10, color: meta.dot, marginLeft: 6}}>•</span>
                    )}
                    <span className="mono" style={{fontSize: 10, color: 'var(--fg-4)', marginLeft: 'auto'}}>{items.length}</span>
                  </button>
                  {!isCollapsed && items.map(c => {
                    const CI = XIcons[c.icon] || XIcons.IconBlocks;
                    const active = c.id === selId;
                    const itemStyle = {
                      display:'flex', alignItems:'center', gap: 10, padding:'10px 12px', borderRadius: 6,
                      background: active
                        ? (meta.hue != null ? meta.bg : 'var(--bg-2)')
                        : 'transparent',
                      border: '1px solid ' + (active
                        ? (meta.hue != null ? meta.border : 'var(--line-2)')
                        : 'transparent'),
                      borderLeft: meta.hue != null ? `2px solid ${active ? meta.dot : 'transparent'}` : '1px solid transparent',
                      cursor:'pointer', textAlign:'left',
                      color: active ? 'var(--fg-0)' : 'var(--fg-2)',
                      transition: 'background .12s, border-color .12s',
                    };
                    return (
                      <button key={c.id} onClick={()=>setSelId(c.id)}
                        className={'cap-item' + (active ? ' is-active' : '')}
                        style={itemStyle}>
                        <div style={{
                          width: 26, height: 26, borderRadius: 5,
                          background: meta.hue != null ? meta.bg : 'var(--bg-3)',
                          color:      meta.hue != null ? meta.fg : 'var(--fg-1)',
                          display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0,
                        }}>
                          <CI size={13}/>
                        </div>
                        <div style={{minWidth: 0, flex: 1}}>
                          <div style={{fontSize: 13, lineHeight: 1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.name}</div>
                          <div className="mono" style={{fontSize: 10, color:'var(--fg-4)', marginTop: 2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.id}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            });
          })()}
        </aside>

        {/* Right: detail + try-it */}
        <div style={{display:'flex', flexDirection:'column', gap: 16}}>
          {/* Header card */}
          <div className="card cap-header-card" style={{padding: 20}}>
            <div className="row cap-header-row" style={{alignItems:'flex-start', gap: 14}}>
              <div style={{width: 40, height: 40, background:'var(--bg-3)', borderRadius: 7, display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0}}>
                <Icon size={18}/>
              </div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="cap-header-titlerow">
                  <div style={{minWidth: 0, flex: 1}}>
                    <h2 style={{margin: 0, fontSize: 20, fontWeight: 500, letterSpacing:'-0.01em'}}>{cap.name}</h2>
                    <p style={{margin: '4px 0 0', fontSize: 13, color:'var(--fg-3)'}}>{cap.desc}</p>
                  </div>
                  <span className="mono tag cap-route-tag" style={{fontSize: 10}}>{cap.route}</span>
                </div>
                <div className="row gap-2" style={{marginTop: 12, flexWrap:'wrap'}}>
                  <span className="micro">{t('cap.routesThrough')}</span>
                  {cap.apis.map(a => (
                    <span key={a} className="mono tag" style={{fontSize: 10, background:'var(--bg-2)'}}>{a}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Two-pane request / response */}
          <div className="cap-pane" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap: 16}}>
            {/* Request */}
            <div className="card" style={{padding: 0, display:'flex', flexDirection:'column'}}>
              <div className="row between" style={{padding:'12px 14px', borderBottom:'1px solid var(--line)'}}>
                <div className="micro">{t('cap.req')}</div>
                {hasKey ? (
                  <button className="btn btn-sm btn-ghost" onClick={()=>navigate('console', {tab:'env'})} title="XAPI_KEY · from Environment tab">
                    <XIcons.IconCheck size={11} style={{color:'var(--ok)'}}/>
                    <span className="mono" style={{fontSize: 10}}>XAPI_KEY</span>
                    <span className="mono" style={{fontSize: 10, color:'var(--fg-4)'}}>{keyPreview}</span>
                  </button>
                ) : (
                  <button className="btn btn-sm" onClick={()=>navigate('console', {tab:'env', focus:'XAPI_KEY'})} style={{borderColor:'var(--warn)', color:'var(--warn)'}}>
                    <span style={{width: 6, height: 6, borderRadius:'50%', background:'var(--warn)'}}/>
                    <span style={{fontSize: 10}}>{t('mcp.auth.set')}</span>
                  </button>
                )}
              </div>
              <div style={{padding: 14, flex: 1}}>
                {cap.inputs.map(f => (
                  <CapField key={f.key} field={f} value={draft[f.key]} onChange={v => setDraft({ [f.key]: v })}/>
                ))}

                {error && (
                  <div className="row gap-2" style={{marginTop: 12, padding:'8px 10px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, color:'var(--err, #f87171)'}}>
                    <XIcons.IconInfo size={12}/>
                    <span>{error}</span>
                  </div>
                )}
              </div>
              <div style={{padding:'12px 14px', borderTop:'1px solid var(--line)'}}>
                {loading ? (
                  <button className="btn" onClick={abort} style={{width:'100%', justifyContent:'center', height: 36, borderColor:'var(--err)', color:'var(--err)'}}>
                    <XIcons.IconPause size={12}/> <span>{t('cap.abort')}</span>
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={send} disabled={!hasKey} style={{width:'100%', justifyContent:'center', height: 36, opacity: !hasKey ? 0.6 : 1}}>
                    {!hasKey ? <><XIcons.IconKey size={12}/> <span>{t('cap.setKeyToSend')}</span></>
                      : <><XIcons.IconPlay size={12}/> <span>{t('cap.run')}</span></>}
                  </button>
                )}
              </div>
            </div>

            {/* Response */}
            <div className="card" style={{padding: 0, display:'flex', flexDirection:'column'}}>
              <div className="row between" style={{padding:'12px 14px', borderBottom:'1px solid var(--line)'}}>
                <div className="micro">{t('cap.resp')}</div>
                {resp && (
                  <div className="row gap-2">
                    <span className="tag mono" style={{fontSize: 10, color:'var(--ok)', borderColor:'color-mix(in srgb, var(--ok) 40%, var(--line-2))'}}>{resp.status}</span>
                    <span className="tag mono" style={{fontSize: 10}}>{resp.latency}ms</span>
                  </div>
                )}
              </div>
              <div style={{padding: 14, flex: 1, minHeight: 260, display:'flex', flexDirection:'column'}}>
                {!resp && !loading && (
                  <div style={{flex: 1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'var(--fg-4)', fontSize: 13, textAlign:'center', padding:'24px 12px', gap: 10}}>
                    <div className="striped hairline" style={{width: 40, height: 40, borderRadius: 7, display:'flex', alignItems:'center', justifyContent:'center'}}>
                      <XIcons.IconPlay size={14}/>
                    </div>
                    <div style={{lineHeight: 1.5, maxWidth: 220}}>{t('cap.placeholder')}</div>
                    <div className="mono" style={{fontSize: 10, color:'var(--fg-4)'}}>POST {cap.route.split(' ')[1]}</div>
                  </div>
                )}
                {loading && (
                  <div style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'var(--fg-3)'}}>
                    <div className="spinner" style={{width: 20, height: 20}}/>
                    <div style={{marginTop: 10, fontSize: 12}}>{t('cap.routing').replace('{APIS}', cap.apis.join(' → '))}</div>
                  </div>
                )}
                {resp && (
                  <pre className="code" style={{margin: 0, flex: 1, overflow:'auto', whiteSpace:'pre-wrap', wordBreak:'break-word', fontSize: 12, lineHeight: 1.55, background:'transparent', border:'none', padding: 0}}>
                    {resp.streaming
                      ? (streamText || resp.body || '(empty stream)')
                      : (typeof resp.body === 'string' ? resp.body : JSON.stringify(resp.body, null, 2))}
                  </pre>
                )}
              </div>
              {resp && resp.body && typeof resp.body === 'object' && (resp.body.usage || resp.body.cost) && (
                <div className="row between" style={{padding:'10px 14px', borderTop:'1px solid var(--line)', fontSize: 11, color:'var(--fg-4)'}}>
                  <span className="mono">{t('cap.charged')}: ${((resp.body.usage && resp.body.usage.cost_usd) || resp.body.cost || 0).toFixed(4)}</span>
                  <span className="mono">{cap.apis.length} {cap.apis.length > 1 ? t('cap.upstreamCalls') : t('cap.upstreamCall')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Request JSON preview */}
          <div className="card" style={{padding: 14}}>
            <div className="row between" style={{marginBottom: 10}}>
              <div className="micro">{t('cap.payload')}</div>
              <span className="micro mono">POST {XApi.config.capabilityBase}{cap.route.split(' ')[1]}</span>
            </div>
            <pre className="code" style={{margin: 0, fontSize: 12, background:'var(--bg-1)', padding: 12, borderRadius: 6, overflow:'auto'}}>
{reqJson}
            </pre>
          </div>
        </div>
      </div>

      {/* Mobile capability picker — bottom sheet that lists all capabilities
          grouped the same way as the desktop sidebar. Tap one to select +
          close the sheet. Portal'd to escape any backdrop-filter ancestor. */}
      {mobilePickerOpen && typeof document !== 'undefined' && createPortal(
        <>
          <div className="mobile-sheet-backdrop" onClick={()=>setMobilePickerOpen(false)} />
          <div className="market-filter-sheet cap-mobile-sheet" role="dialog" aria-label={t('cap.skills')} onClick={(e)=>e.stopPropagation()}>
            <div className="row between" style={{ alignItems:'center', marginBottom: 14 }}>
              <div className="micro">{list.length} {t('cap.skills')}</div>
              <button onClick={()=>setMobilePickerOpen(false)} className="btn btn-sm btn-ghost btn-icon" aria-label="Close">
                <XIcons.IconX size={12}/>
              </button>
            </div>
            <div className="col" style={{gap: 4}}>
              {(() => {
                const buckets = new Map();
                for (const c of list) {
                  const key = c.idPrefix || 'other';
                  if (!buckets.has(key)) buckets.set(key, []);
                  buckets.get(key).push(c);
                }
                const groups = Array.from(buckets.entries())
                  .map(([prefix, items]) => ({ prefix, items }))
                  .sort((a, b) => b.items.length - a.items.length || a.prefix.localeCompare(b.prefix));
                return groups.map(({ prefix, items }) => {
                  const meta = capCategoryMeta(prefix);
                  return (
                    <div key={prefix} style={{marginBottom: 10}}>
                      <div className="row gap-2" style={{
                        alignItems:'center', padding:'8px 4px 6px',
                        borderTop: `1px dashed ${meta.border}`,
                      }}>
                        <span style={{width: 6, height: 6, borderRadius:'50%', background: meta.dot}}/>
                        <span className="mono" style={{
                          fontSize: 10, letterSpacing: '0.08em', textTransform:'uppercase',
                          color: meta.hue != null ? meta.fg : 'var(--fg-3)',
                        }}>{prefix}</span>
                        <span className="mono" style={{fontSize: 10, color: 'var(--fg-4)', marginLeft: 'auto'}}>{items.length}</span>
                      </div>
                      {items.map(c => {
                        const CI = XIcons[c.icon] || XIcons.IconBlocks;
                        const active = c.id === selId;
                        return (
                          <button key={c.id}
                            onClick={()=>{ setSelId(c.id); setMobilePickerOpen(false); }}
                            className={'cap-item' + (active ? ' is-active' : '')}
                            style={{
                              display:'flex', alignItems:'center', gap: 10,
                              padding:'12px 12px',
                              borderRadius: 6, width:'100%',
                              background: active ? (meta.hue != null ? meta.bg : 'var(--bg-2)') : 'transparent',
                              border: '1px solid ' + (active ? (meta.hue != null ? meta.border : 'var(--line-2)') : 'transparent'),
                              borderLeft: meta.hue != null ? `2px solid ${active ? meta.dot : 'transparent'}` : '1px solid transparent',
                              cursor:'pointer', textAlign:'left',
                              color: active ? 'var(--fg-0)' : 'var(--fg-2)',
                            }}>
                            <div style={{
                              width: 28, height: 28, borderRadius: 5,
                              background: meta.hue != null ? meta.bg : 'var(--bg-3)',
                              color:      meta.hue != null ? meta.fg : 'var(--fg-1)',
                              display:'flex', alignItems:'center', justifyContent:'center', flexShrink: 0,
                            }}>
                              <CI size={13}/>
                            </div>
                            <div style={{minWidth: 0, flex: 1}}>
                              <div style={{fontSize: 14, lineHeight: 1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.name}</div>
                              <div className="mono" style={{fontSize: 10.5, color:'var(--fg-4)', marginTop: 2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.id}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

const CapField = ({ field: f, value, onChange }) => {
  const { t } = useT();
  const id = 'cap-f-' + f.key;
  const val = value != null ? value : (f.default != null ? f.default : (f.type === 'boolean' ? false : (f.type === 'multi' ? [] : '')));
  const label = (
    <label htmlFor={id} className="field-label" style={{display:'flex', gap: 6, alignItems:'baseline', flexWrap:'wrap', lineHeight: 1.3}}>
      <span className="mono" style={{color:'var(--fg-2)'}}>{f.key}</span>
      <span style={{fontSize: 10, color:'var(--fg-4)', whiteSpace:'nowrap'}}>{f.type}{f.required ? ' · ' + t('cap.required') : ''}</span>
    </label>
  );

  let control;
  if (f.type === 'string' || f.type === 'number') {
    control = <input id={id} className="input" type={f.type === 'number' ? 'number' : 'text'} value={val} placeholder={f.placeholder || ''}
      onChange={e => onChange(f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}/>;
  } else if (f.type === 'text') {
    control = <textarea id={id} className="input" rows={f.rows || 3} value={val} placeholder={f.placeholder || ''} onChange={e=>onChange(e.target.value)}/>;
  } else if (f.type === 'json') {
    // 复杂类型：原始字符串自由编辑，提交前 parse；这里做内联语法校验提示。
    const raw = typeof val === 'string' ? val : (val != null ? JSON.stringify(val, null, 2) : '');
    let parseErr = null;
    if (raw.trim()) { try { JSON.parse(raw); } catch (e) { parseErr = e.message; } }
    control = (
      <>
        <textarea id={id} className="input mono" rows={f.rows || 6} value={raw} placeholder={f.hint || f.placeholder || '[]'}
          onChange={e=>onChange(e.target.value)} style={{fontSize: 12, lineHeight: 1.5}}/>
        {parseErr && <div className="micro" style={{marginTop: 4, color:'var(--danger, #e5534b)'}}>JSON 语法错误：{parseErr}</div>}
      </>
    );
  } else if (f.type === 'boolean') {
    control = (
      <div className="row gap-2" style={{alignItems:'center'}}>
        <input id={id} type="checkbox" checked={!!val} onChange={e=>onChange(e.target.checked)} style={{accentColor:'#fff'}}/>
        <label htmlFor={id} style={{fontSize: 12, color:'var(--fg-3)'}}>{val ? 'true' : 'false'}</label>
      </div>
    );
  } else if (f.type === 'enum') {
    control = (
      <div className="row gap-2" style={{flexWrap:'wrap'}}>
        {f.options.map(opt => (
          <button key={opt} onClick={()=>onChange(opt)} className={'btn btn-sm' + (val === opt ? ' btn-primary' : '')} style={{fontSize: 11}}>{opt}</button>
        ))}
      </div>
    );
  } else if (f.type === 'multi') {
    const arr = Array.isArray(val) ? val : [];
    control = (
      <div className="row gap-2" style={{flexWrap:'wrap'}}>
        {f.options.map(opt => {
          const on = arr.includes(opt);
          return (
            <button key={opt} onClick={()=>onChange(on ? arr.filter(x=>x!==opt) : [...arr, opt])} className={'btn btn-sm' + (on ? ' btn-primary' : '')} style={{fontSize: 11}}>
              {on && <XIcons.IconCheck size={10}/>} {opt}
            </button>
          );
        })}
      </div>
    );
  } else {
    control = <input id={id} className="input" value={val} onChange={e=>onChange(e.target.value)}/>;
  }

  return (
    <div style={{marginBottom: 14}}>
      {label}
      <div style={{marginTop: 6}}>{control}</div>
      {f.desc && <div className="micro" style={{marginTop: 4, color:'var(--fg-4)'}}>{f.desc}</div>}
    </div>
  );
};


// Backward-compat alias: anywhere still using MyApisPage resolves to the new WorkspacePage.
const MyApisPage = WorkspacePage;

export const XOtherPages = { MCPPage, WorkspacePage, MyApisPage, AgentImportPage, AdminPage, LoginPage, TopUpPage, CapabilityPage };
export { MCPPage, WorkspacePage, MyApisPage, AgentImportPage, AdminPage, LoginPage, TopUpPage, CapabilityPage };
if (typeof window !== 'undefined') window.XOtherPages = XOtherPages;
